// =============================================
// Masters → Vendors, Customers & Other Parties
// =============================================
// The name lists bookkeeping is built on: who you buy from (acctg_vendor), who you sell
// to (acctg_customer) and everyone else on the other side of an entry — the bank, a tax
// authority, a shareholder, a landlord (acctg_other_party, migration 114).
//
// Each list belongs to ONE set of books (owner entity) inside the account, exactly like
// the chart of accounts. Soft-delete only; a new party starts in the entity's base
// currency. A vendor can be filled from the details already captured off receipts
// (the sitewide vendor master — public business facts only), and the Vendors list can be
// populated from the parties on documents already FILED for those books.
//
// SHARED entries (migration 115): owner_type 'shared', owner_id = the account owner. One
// record with one set of values, listed in EVERY set of the account's own books — the
// bank or Costco is entered once, not once per person and company. Own books only:
// a client's books never see them.
// =============================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRefreshState, readRefreshValue, consumeRefreshValue, writeRefreshValue } from '../../../lib/refreshState';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiDownloadCloud, FiFileText, FiCheckSquare } from 'react-icons/fi';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import { loadTable, saveRow, addRow, getTableData } from '../../db-viewer/data/dataAccess';
import { confirmDiscardChanges, setFormDirty, registerSaveHandler, submitSaver } from '../../../lib/unsavedChanges';
import { addNotification } from '../../../lib/notifications';
import apiClient from '../../../lib/api/apiClient';
import {
  OwnerEntity, getCurrentUser, getAccountOwnerForUser, resolveEntities,
} from '../utils/ownerEntities';
import { entityBaseCurrency } from '../utils/baseCurrency';
import { partySideOf } from '../utils/partySide';
import { rankNames } from '../utils/nameSearch';
import { planDocTypeLearning, PartyDocType } from '../utils/partyDocType';
import { naturesForAccount } from '../utils/docNature';
import { partyOpeningFromJournal } from '../utils/partyLedger';
import PartyAttachments, { AttachmentLine } from './PartyAttachments';
import PartyReconcileModal from './PartyReconcileModal';
import PartyTip from './PartyTip';
import SampleReceiptPicker, { docLabel, isPartyDoc } from './SampleReceiptPicker';
import TagInput from '../../../components/ui/TagInput';
import { parseTags, joinTags, recordTagUse } from '../../../lib/tagStore';
import { effectiveDocuTypes } from '../utils/docKind';
import './PartiesMasterTab.css';

type SortCol = 'txns' | 'name' | 'type' | 'contact' | 'phone' | 'email' | 'city' | 'terms' | 'currency' | 'reconciled';

type Row = Record<string, any>;
export type PartyKind = 'vendor' | 'customer' | 'other';
type Kind = PartyKind;

interface KindDef {
  key: Kind;
  table: string;
  pk: string;
  nameCol: string;
  codeCol: string;
  singular: string;
  plural: string;
  blurb: string;
}

const KINDS: KindDef[] = [
  { key: 'vendor',   table: 'acctg_vendor',      pk: 'vendor_id',      nameCol: 'vendor_name',   codeCol: 'vendor_code',   singular: 'Vendor',      plural: 'Vendors',       blurb: 'Suppliers you buy goods and services from.' },
  { key: 'customer', table: 'acctg_customer',    pk: 'customer_id',    nameCol: 'customer_name', codeCol: 'customer_code', singular: 'Customer',    plural: 'Customers',     blurb: 'People and businesses you sell to.' },
  { key: 'other',    table: 'acctg_other_party', pk: 'other_party_id', nameCol: 'party_name',    codeCol: 'party_code',    singular: 'Other Party', plural: 'Other Parties', blurb: 'Anyone else on the other side of an entry — bank, tax authority, shareholder, landlord, insurer.' },
];

// Neutral, country-agnostic groupings (stored as the code).
const PARTY_TYPES: { value: string; label: string }[] = [
  { value: 'bank',            label: 'Bank / Lender' },
  { value: 'government',      label: 'Government / Tax authority' },
  { value: 'shareholder',     label: 'Shareholder / Owner' },
  { value: 'director',        label: 'Director / Officer' },
  { value: 'employee',        label: 'Employee' },
  { value: 'landlord',        label: 'Landlord' },
  { value: 'insurance',       label: 'Insurance' },
  { value: 'related_company', label: 'Related company' },
  { value: 'other',           label: 'Other' },
];

const TERMS = ['Due on receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'COD', 'Prepaid'];

interface PartyForm {
  name: string;                    // PARTY NAME — what bookkeeping and DMS display
  legal_name: string;              // registered name (statements, year-end slips)
  nickname: string;                // short name used when naming files
  alias_names: string;             // other names the same party goes by (comma-separated)
  tags: string;                    // free labels to find it by (comma list; account tag dictionary)
  code: string;
  party_type: string;
  contact_name: string;
  contact_title: string;
  email: string;
  phone: string;                   // office line
  mobile: string;                  // business cell
  fax: string;
  website: string;
  contact_2_name: string;
  contact_2_phone: string;
  contact_2_email: string;
  // The business MAILING address (remit-to / ship-to) and the contact person's HOME
  // address — the same fields as the physical address (migration 126).
  mail_address_1: string; mail_address_2: string; mail_city: string; mail_state_province: string; mail_postal_code: string; mail_country: string;
  home_address_1: string; home_address_2: string; home_city: string; home_state_province: string; home_postal_code: string; home_country: string;
  folder_id: string;               // related DMS folder (any level); '' = none
  account_number: string;
  tax_id_2: string;
  default_coa_id: string;
  default_payment_method_id: string;
  credit_limit: string;
  opening_balance: string;
  opening_balance_date: string;
  tax_slip_required: number;
  reconcile_required: number;      // 1 = reconciled against its statement on a periodic basis
  ownerPicked: boolean;            // a new entry made from "All … from all books": whose books was chosen
  moveTo: string;                  // editing: another set of books to move the entry to ('type:id'; '' = stay)
  address_1: string;
  address_2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  tax_id: string;
  payment_terms: string;
  default_currency: string;
  notes: string;
  is_active: number;
  global_busi_id: number | null;   // vendors only: link to the sitewide vendor master
  shared: boolean;                 // available in all of the account's own books
}

const EMPTY: PartyForm = {
  name: '', legal_name: '', nickname: '', alias_names: '', tags: '', code: '', party_type: 'other',
  contact_name: '', contact_title: '', email: '', phone: '', mobile: '', fax: '', website: '',
  contact_2_name: '', contact_2_phone: '', contact_2_email: '', folder_id: '',
  mail_address_1: '', mail_address_2: '', mail_city: '', mail_state_province: '', mail_postal_code: '', mail_country: '',
  home_address_1: '', home_address_2: '', home_city: '', home_state_province: '', home_postal_code: '', home_country: '',
  account_number: '', tax_id_2: '', default_coa_id: '', default_payment_method_id: '',
  credit_limit: '', opening_balance: '', opening_balance_date: '', tax_slip_required: 0, reconcile_required: 0, ownerPicked: false, moveTo: '',
  address_1: '', address_2: '', city: '', state_province: '', postal_code: '', country: '',
  tax_id: '', payment_terms: '', default_currency: '', notes: '', is_active: 1, global_busi_id: null, shared: false,
};

// Quick add: only the form, opened on a new entry — used where a party is needed while
// working on something else (Process Docs → Bookkeeping → Party Name).
export interface PartyQuickAdd {
  kinds: PartyKind[];            // the lists an entry may be added to here; the first is offered
  ownerKey?: string;             // whose books ('owner_type:owner_id'); default = the first
  name?: string;                 // party name to start with
  onSaved: (kind: PartyKind, name: string) => void;
  onCancel: () => void;
}

interface PartiesMasterTabProps {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
  quickAdd?: PartyQuickAdd;
}

// One line of the form's "Document types" block (acctg_party_doc_type, migration 123).
interface DocTypeLine {
  id: number | null;        // null = not saved yet
  name: string;
  words: string;            // words printed on that kind of document (comma-separated)
  format: string;           // date_format_id as a string; '' = not set
  sample: string;           // document_id of a receipt of this kind; '' = none
  nature: string;           // nature_id (acctg_doc_nature, migration 142); '' = not set
  coa: string;              // account head coa_id in these books; '' = not set (none when shared)
  removed?: boolean;
}

// The address fields, shared by the three address blocks: '' = physical, 'mail_', 'home_'.
type AddrPrefix = '' | 'mail_' | 'home_';
const ADDR_PARTS = ['address_1', 'address_2', 'city', 'state_province', 'postal_code', 'country'] as const;

const NOW_ISO = () => new Date().toISOString();
const SHARED = 'shared';
const ALL = 'all';                 // the landing view: every entry of the account's own books (not a real owner)
const UNVERIFIED = 'unverified';   // created from a receipt, not yet checked by a person (migration 124)
const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

const PartiesMasterTab: React.FC<PartiesMasterTabProps> = ({ viewMode = 'owner', selectedClientId = null, quickAdd }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [usersData, setUsersData] = useState<Row[]>([]);
  const [businessesData, setBusinessesData] = useState<Row[]>([]);
  const [rowsByKind, setRowsByKind] = useState<Record<Kind, Row[]>>({ vendor: [], customer: [], other: [] });
  const [currencies, setCurrencies] = useState<Row[]>([]);
  const [countries, setCountries] = useState<string[]>([]);

  // A browser refresh comes back to the same list, books and open entry (lib/refreshState);
  // never in the quick-add popup, which always starts fresh.
  const [kind, setKind] = useRefreshState<Kind>('parties.kind', quickAdd?.kinds[0] || 'vendor',
    v => !quickAdd && KINDS.some(k => k.key === v));
  const [ownerKey, setOwnerKey] = useRefreshState<string>('parties.books', quickAdd?.ownerKey || '',
    v => !quickAdd && typeof v === 'string');
  const [search, setSearch] = useState('');
  // Column sort picked from the list's headers. Default: by name (owner, 2026-10-17). Whatever
  // the sort, entries still to check (added from Process Docs, shown in red) stay on top.
  const [sortCol, setSortCol] = useState<SortCol | null>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showInactive, setShowInactive] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);   // names dropdown under the search box
  const [suggestIdx, setSuggestIdx] = useState(-1);

  const [form, setForm] = useState<PartyForm | null>(null);   // null = no form open
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | string | null>(null);
  const [lookupMsg, setLookupMsg] = useState('');
  // Document types of the party in the form, each with its date layout.
  const [docTypeRows, setDocTypeRows] = useState<Row[]>([]);
  const [dateFormats, setDateFormats] = useState<Row[]>([]);
  const [natureRows, setNatureRows] = useState<Row[]>([]);   // Nature of Document list (migration 142)
  const [docTypes, setDocTypes] = useState<DocTypeLine[]>([]);
  const [viewDocId, setViewDocId] = useState<number | null>(null);   // sample receipt shown in the popup
  const [pickSampleFor, setPickSampleFor] = useState<number | null>(null); // document-type line whose sample is being chosen
  // Provinces / territories / states per country, for the address.
  const [countryRows, setCountryRows] = useState<Row[]>([]);
  const [stateRows, setStateRows] = useState<Row[]>([]);
  // Files kept with the party in the form (acctg_party_attachment).
  const [attachmentRows, setAttachmentRows] = useState<Row[]>([]);
  const [attachments, setAttachments] = useState<AttachmentLine[]>([]);
  // Journals: a party's opening balance can come from an opening-balances entry.
  const [journals, setJournals] = useState<Row[]>([]);
  const [journalLines, setJournalLines] = useState<Row[]>([]);
  const [reconcileRow, setReconcileRow] = useState<Row | null>(null);   // the party whose balance is being reconciled
  const [reconcileRows, setReconcileRows] = useState<Row[]>([]);        // acctg_party_reconcile — for the list's "Reconciled" column
  const formRef = useRef<PartyForm | null>(null);
  const fillRef = useRef<(auto?: boolean, name?: string) => void>(() => {});
  const returnKeyRef = useRef('');   // the view to go back to after editing an entry opened from "All … from all books"
  // The entry that was open in the form before a refresh (re-opened once the lists load).
  const [refreshOpen, setRefreshOpen] = useState(() =>
    quickAdd ? null : readRefreshValue<{ kind: Kind; id: number | string; returnKey?: string }>('parties.open') ?? null);
  useEffect(() => { consumeRefreshValue('parties.open'); }, []);

  // Populate Vendors from filed documents: the parties named on this set of books'
  // documents that are not vendors yet.
  const [documents, setDocuments] = useState<Row[]>([]);
  const [docOwners, setDocOwners] = useState<Row[]>([]);
  const [docuTypeRows, setDocuTypes] = useState<Row[]>([]);   // raw acctg_docu_type
  const [importOpen, setImportOpen] = useState(false);
  // Pickers on the form: the party's DMS folder, default account and payment method.
  const [foldersData, setFoldersData] = useState<Row[]>([]);
  const [coaData, setCoaData] = useState<Row[]>([]);
  const [payMethods, setPayMethods] = useState<Row[]>([]);
  const [importPicked, setImportPicked] = useState<Set<string>>(new Set());

  const def = KINDS.find(k => k.key === kind)!;

  // ---- Load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const safe = (t: string) => loadTable(t).catch(() => getTableData(t));
      const [u, b, v, c, o, cur, ctry, docs, dOwners, dTypes] = await Promise.all([
        safe('users'), safe('user_businesses'), safe('acctg_vendor'), safe('acctg_customer'),
        safe('acctg_other_party'), safe('site_mstr_currencies'), safe('site_mstr_countries'),
        safe('dms_documents'), safe('dms_document_owners'), safe('acctg_docu_type'),
      ]);
      const [fold, coa, pay, pdt, dfm] = await Promise.all([
        safe(viewMode === 'clients' ? 'dms_client_folders' : 'dms_folders'), safe('acctg_coa'), safe('acctg_payment_method'),
        safe('acctg_party_doc_type'), safe('site_mstr_date_formats'),
      ]);
      const [sts, att, jr, jl, nat] = await Promise.all([
        safe('site_mstr_states'), safe('acctg_party_attachment'), safe('acctg_gl_journal'), safe('acctg_gl_journal_line'),
        safe('acctg_doc_nature'),
      ]);
      if (cancelled) return;
      setStateRows(sts); setAttachmentRows(att); setJournals(jr); setJournalLines(jl); setCountryRows(ctry);
      loadTable('acctg_party_reconcile').catch(() => getTableData('acctg_party_reconcile')).then(r => { if (!cancelled) setReconcileRows(r); });
      setFoldersData(fold); setCoaData(coa); setPayMethods(pay);
      setDocTypeRows(pdt); setDateFormats(dfm); setNatureRows(nat);
      // Only the few fields the import needs — never the file bytes.
      setDocuments(docs.map((d: Row) => ({
        document_id: d.document_id, account_owner_id: d.account_owner_id, filename: d.filename,
        owner_type: d.owner_type, owner_id: d.owner_id, status: d.status,
        party_name: d.party_name, docu_type_id: d.docu_type_id, date_of_document: d.date_of_document, date_format_id: d.date_format_id,
        global_busi_id: d.global_busi_id, is_deleted: d.is_deleted, total_amount: d.total_amount,
      })));
      setDocOwners(dOwners); setDocuTypes(dTypes);
      setUsersData(u); setBusinessesData(b);
      setRowsByKind({ vendor: v, customer: c, other: o });
      setCurrencies(cur);
      setCountries(ctry.map((r: Row) => r.name).filter(Boolean).sort());
      setDataLoaded(true);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentUser = useMemo(() => (dataLoaded ? getCurrentUser(usersData) : null), [usersData, dataLoaded]);
  const accountOwnerId = useMemo(() => {
    if (!currentUser) return 0;
    const ao = getAccountOwnerForUser(currentUser.user_id, usersData);   // the logged-in user's own account
    return ao ? Number(ao.user_id) : 0;
  }, [currentUser, usersData]);
  // the kinds as this account has them (own kinds take their base kind's code — utils/docKind)
  const docuTypes = useMemo<Row[]>(() => effectiveDocuTypes(docuTypeRows, accountOwnerId || null), [docuTypeRows, accountOwnerId]);

  const ownerEntities = useMemo<OwnerEntity[]>(
    () => (dataLoaded ? resolveEntities(viewMode, accountOwnerId, selectedClientId, usersData, businessesData) : []),
    [dataLoaded, viewMode, accountOwnerId, selectedClientId, usersData, businessesData]);

  // "All <Vendors> from all books" is listed first and is where the page opens; then
  // "Shared (all books)"; then the real sets of books — own books only.
  const canShare = viewMode === 'owner' && accountOwnerId > 0;
  const sharedEntity = useMemo<OwnerEntity>(
    () => ({ owner_type: SHARED, owner_id: accountOwnerId, display_name: 'Shared (all books)', badge: 'Shared' }),
    [accountOwnerId]);
  const allEntity = useMemo<OwnerEntity>(
    () => ({ owner_type: ALL, owner_id: accountOwnerId, display_name: `All ${def.plural} from all books`, badge: 'All' }),
    [accountOwnerId, def]);
  // Quick add (a party needed while filing) offers only places an entry can be saved to.
  const bookEntities = useMemo<OwnerEntity[]>(
    () => (canShare && ownerEntities.length ? [...(quickAdd ? [] : [allEntity]), sharedEntity, ...ownerEntities] : ownerEntities),
    [canShare, ownerEntities, sharedEntity, allEntity, quickAdd]);

  useEffect(() => {
    // Opens on the first entry — "All … from all books" for own books, the first set otherwise.
    if (bookEntities.length && !bookEntities.some(e => `${e.owner_type}:${e.owner_id}` === ownerKey)) {
      setOwnerKey(`${bookEntities[0].owner_type}:${bookEntities[0].owner_id}`);
    }
  }, [bookEntities, ownerKey]);

  const entity = useMemo(() => bookEntities.find(e => `${e.owner_type}:${e.owner_id}` === ownerKey) || null, [bookEntities, ownerKey]);
  const inAllView = entity?.owner_type === ALL;
  // A new entry made from "All … from all books" is a shared one unless books are chosen
  // for it, so the two views behave alike for the form.
  const inSharedView = entity?.owner_type === SHARED || inAllView;
  const baseCurrency = useMemo(
    () => (entity ? entityBaseCurrency(entity.owner_type, entity.owner_id, usersData, businessesData) : ''),
    [entity, usersData, businessesData]);

  // Rows of one kind that belong to the selected books (not deleted).
  const entityRows = useCallback((k: Kind): Row[] => {
    if (!entity) return [];
    return rowsByKind[k].filter(r => {
      // eslint-disable-next-line eqeqeq
      if (r.account_owner_id != accountOwnerId || r.is_deleted == 1) return false;
      // eslint-disable-next-line eqeqeq
      if (r.owner_type === entity.owner_type && r.owner_id == entity.owner_id) return true;
      // "All … from all books" is the landing view and lists EVERY entry of the account's
      // own books — shared ones and each person's / business's (badged with its books) — so
      // an entry never drops out of sight, e.g. once it has been checked and saved.
      if (entity.owner_type === ALL) return !String(r.owner_type || '').startsWith('client_');
      return canShare && r.owner_type === SHARED;          // shared entries show in every own set of books
    });
  }, [rowsByKind, entity, accountOwnerId, canShare]);

  // Every entry of a kind across the account's OWN books (shared + each person/business).
  const ownBooksRows = useCallback((k: Kind): Row[] => rowsByKind[k].filter(r =>
    // eslint-disable-next-line eqeqeq
    r.account_owner_id == accountOwnerId && r.is_deleted != 1 && !String(r.owner_type || '').startsWith('client_')),
  [rowsByKind, accountOwnerId]);

  // The opening balance an opening-balances journal entry gives the party being edited
  // (in its own books; any of the account's books for a shared entry). When there is
  // one, it IS the opening balance — the typed figure is not used.
  const journalOpening = useMemo(() => {
    if (editingId == null || !entity) return null;
    const books = inSharedView ? null : { account_owner_id: accountOwnerId, owner_type: entity.owner_type, owner_id: entity.owner_id };
    return partyOpeningFromJournal(journals as any[], journalLines as any[], kind, editingId, books);
  }, [editingId, entity, inSharedView, accountOwnerId, journals, journalLines, kind]);

  // Provinces / territories / states of a country (by its name), for the address.
  const statesOf = useCallback((countryName: string) => {
    const c = countryRows.find(x => String(x.name || '').toLowerCase() === String(countryName || '').trim().toLowerCase());
    if (!c) return [] as { name: string; code: string; type: string }[];
    return stateRows
      // eslint-disable-next-line eqeqeq
      .filter(x => x.countries_id == c.countries_id && x.is_deleted != 1 && x.is_active != 0 && x.state_name)
      .map(x => ({ name: String(x.state_name), code: String(x.state_code || '').split('-').pop() || '', type: String(x.state_type || 'State') }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [countryRows, stateRows]);

  // The latest reconciliation of each party of the list in view (any of its books):
  // party id → { date, difference }.
  const lastReconciled = useMemo(() => {
    const m = new Map<string, { date: string; difference: number }>();
    reconcileRows.forEach(r => {
      // eslint-disable-next-line eqeqeq
      if (r.is_deleted == 1 || r.party_kind !== kind || r.account_owner_id != accountOwnerId) return;
      const date = String(r.statement_date || '').slice(0, 10);
      const cur = m.get(String(r.party_id));
      if (!cur || date > cur.date) m.set(String(r.party_id), { date, difference: Number(r.difference) || 0 });
    });
    return m;
  }, [reconcileRows, kind, accountOwnerId]);

  // Header click: a new column sorts A→Z, again Z→A, a third time back to the default (name A→Z).
  const toggleSort = (col: SortCol) => {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortCol('name'); setSortDir('asc');
  };
  const sortHeader = (col: SortCol, label: React.ReactNode, title?: string) => {
    const active = sortCol === col;
    return (
      <th aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'} title={title}>
        <button type="button" className={`pm-sort-btn ${active ? 'active' : ''}`} onClick={() => toggleSort(col)}
          title={title || (active && sortDir === 'desc' ? 'Click for the default order' : 'Click to sort')}>
          {label}
          <span className="pm-sort-arrow" aria-hidden="true">{active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
        </button>
      </th>
    );
  };

  // Whose books an entry belongs to, for its badge: the owner's NICKNAME (a person's
  // profile nickname / a business's acronym, e.g. "1011") — the full name only when
  // there is none. The full name is always in the tooltip.
  const ownerLabel = (r: Row): { short: string; full: string } => {
    // eslint-disable-next-line eqeqeq
    const full = bookEntities.find(e => e.owner_type === r.owner_type && e.owner_id == r.owner_id)?.display_name || 'Other books';
    let nick = '';
    if (r.owner_type === 'business' || r.owner_type === 'client_business') {
      // eslint-disable-next-line eqeqeq
      const b = businessesData.find(x => x.business_id == r.owner_id);
      nick = b ? String(b.busi_acronym || b.acronym || b.busi_acro || '') : '';
    } else {
      // eslint-disable-next-line eqeqeq
      const u = usersData.find(x => x.user_id == r.owner_id);
      nick = u ? String(u.nickname || '') : '';
    }
    return { short: nick.trim() || full, full };
  };

  // Names offered under the search box while typing: those that START with what was typed,
  // then those that contain it (name, legal name, nickname, an alias), then — when the
  // spelling is off (Clee for Clea…, Micheal for Michael) — names a typing slip away.
  const searchRanked = useMemo(() => rankNames(search, entityRows(kind)
    // eslint-disable-next-line eqeqeq
    .filter(r => showInactive || r.is_active != 0)
    .map(r => ({ item: r, name: String(r[def.nameCol] || ''), others: [r.legal_name, r.nickname, ...String(r.alias_names || '').split(',')].map(v => String(v || '')) }))),
  [entityRows, kind, def, search, showInactive]);
  const searchSuggestions = useMemo(
    () => [...searchRanked.starts, ...searchRanked.contains, ...searchRanked.similar].slice(0, 12), [searchRanked]);
  const firstSimilar = searchRanked.starts.length + searchRanked.contains.length;   // where "Did you mean" begins

  const pickSuggestion = (r: Row) => {
    setSearch(String(r[def.nameCol] || ''));
    setSuggestOpen(false); setSuggestIdx(-1);
  };

  const currencyOptions = useMemo(() => currencies
    // eslint-disable-next-line eqeqeq
    .filter(c => c.is_deleted != 1 && c.currency_code)
    .map(c => ({ value: String(c.currency_code).trim().toUpperCase(), label: `${String(c.currency_code).trim().toUpperCase()} — ${c.currency_name || ''}` }))
    .sort((a, b) => a.value.localeCompare(b.value)), [currencies]);

  // The account's DMS folders (any level), shown with their full path. Own books use
  // dms_folders; a client's books use that client's dms_client_folders.
  const folderOptions = useMemo(() => {
    const live = foldersData.filter(f =>
      // eslint-disable-next-line eqeqeq
      f.account_owner_id == accountOwnerId && f.is_deleted != 1 && f.is_active != 0 &&
      // eslint-disable-next-line eqeqeq
      (viewMode !== 'clients' || selectedClientId == null || f.client_id == selectedClientId));
    const pathOf = (f: Row) => {
      const parts = [String(f.folder_name || '')];
      const seen = new Set<any>([f.folder_id]);
      let cur = f;
      while (cur.parent_folder_id != null && !seen.has(cur.parent_folder_id)) {
        seen.add(cur.parent_folder_id);
        // eslint-disable-next-line eqeqeq
        const parent = live.find(x => x.folder_id == cur.parent_folder_id);
        if (!parent) break;
        parts.unshift(String(parent.folder_name || ''));
        cur = parent;
      }
      return parts.join(' > ');
    };
    return live.map(f => ({ value: String(f.folder_id), label: pathOf(f) })).sort((a, b) => a.label.localeCompare(b.label));
  }, [foldersData, accountOwnerId, viewMode, selectedClientId]);

  // Posting accounts of the selected books (a shared entry has no single chart to point at).
  const coaOptions = useMemo(() => {
    if (!entity || inSharedView) return [];
    return coaData
      .filter(a =>
        // eslint-disable-next-line eqeqeq
        a.account_owner_id == accountOwnerId && a.owner_type === entity.owner_type && a.owner_id == entity.owner_id &&
        // eslint-disable-next-line eqeqeq
        a.is_deleted != 1 && a.is_header != 1)
      .map(a => ({ value: String(a.coa_id), label: `${a.account_number || ''} — ${a.account_name || ''}`.trim() }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [coaData, entity, inSharedView, accountOwnerId]);

  // Nature of Document list (system + this account's own) for the document-type lines.
  const natureOptions = useMemo(() => naturesForAccount(natureRows, accountOwnerId || null)
    .map(n => ({ value: String(n.nature_id), label: n.nature_name, keywords: n.match_words })), [natureRows, accountOwnerId]);

  const payMethodOptions = useMemo(() => payMethods
    // eslint-disable-next-line eqeqeq
    .filter(m => m.is_deleted != 1 && m.is_active != 0)
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
    .map(m => ({ value: String(m.payment_method_id), label: String(m.method_name || '') })), [payMethods]);

  // Which list a filed document's party belongs in, from the document type: money going
  // out → a vendor; money coming in → a customer; a report / other document → could be
  // either (treated as a vendor when added automatically). Nothing says "other party".
  const sideOfDoc = useCallback((d: Row) => {
    // eslint-disable-next-line eqeqeq
    const type = docuTypes.find(t => t.docu_type_id == d.docu_type_id);
    return partySideOf(type ? type.docu_type_code : '');
  }, [docuTypes]);
  const docFitsKind = useCallback((d: Row, k: Kind) => {
    const side = sideOfDoc(d);
    return k === 'vendor' ? side !== 'customer' : k === 'customer' ? side !== 'vendor' : true;
  }, [sideOfDoc]);
  // Every name a row answers to (party name, legal name, nickname, aliases), lower-cased.
  const rowNames = (r: Row, k: Kind): string[] => {
    const d = KINDS.find(x => x.key === k)!;
    return [r[d.nameCol], r.legal_name, r.nickname, ...String(r.alias_names || '').split(',')].map(v => String(v == null ? '' : v).trim().toLowerCase()).filter(Boolean);
  };

  // How much each entry of the list in view has been used: documents naming it (filed or
  // still to file, in the entry's own books — any own books for a shared one) and journal
  // entries whose lines are tagged with it. Bookkeeping postings from documents carry no
  // party, so the two never count the same thing twice.
  const txnCounts = useMemo(() => {
    const m = new Map<string, { docs: number; toFile: number; journals: number }>();
    const ownersByDoc = new Map<number, Set<string>>();
    docOwners.forEach(o => {
      // eslint-disable-next-line eqeqeq
      if (o.is_deleted == 1) return;
      const k = Number(o.document_id);
      if (!ownersByDoc.has(k)) ownersByDoc.set(k, new Set());
      ownersByDoc.get(k)!.add(`${o.owner_type}:${o.owner_id}`);
    });
    const liveJournals = new Set(journals
      // eslint-disable-next-line eqeqeq
      .filter(j => j.is_deleted != 1 && j.account_owner_id == accountOwnerId).map(j => Number(j.journal_id)));
    const docsByName = new Map<string, Row[]>();
    documents.forEach(d => {
      // eslint-disable-next-line eqeqeq
      if (d.is_deleted == 1 || d.account_owner_id != accountOwnerId || !docFitsKind(d, kind)) return;
      const n = String(d.party_name || '').trim().toLowerCase();
      if (!n) return;
      if (!docsByName.has(n)) docsByName.set(n, []);
      docsByName.get(n)!.push(d);
    });
    rowsByKind[kind].forEach(r => {
      // eslint-disable-next-line eqeqeq
      if (r.is_deleted == 1 || r.account_owner_id != accountOwnerId) return;
      const seen = new Set<number>();
      let docs = 0; let toFile = 0;
      rowNames(r, kind).forEach(n => (docsByName.get(n) || []).forEach(d => {
        const id = Number(d.document_id);
        if (seen.has(id)) return;
        // eslint-disable-next-line eqeqeq
        const inBooks = r.owner_type === SHARED || (d.owner_type === r.owner_type && d.owner_id == r.owner_id)
          || !!ownersByDoc.get(id)?.has(`${r.owner_type}:${r.owner_id}`);
        if (!inBooks) return;
        seen.add(id);
        docs++;
        if (d.status !== 'Filed') toFile++;
      }));
      const jset = new Set<number>();
      journalLines.forEach(l => {
        // eslint-disable-next-line eqeqeq
        if (l.is_deleted != 1 && l.party_kind === kind && l.party_id == r[def.pk] && liveJournals.has(Number(l.journal_id))) jset.add(Number(l.journal_id));
      });
      m.set(String(r[def.pk]), { docs, toFile, journals: jset.size });
    });
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsByKind, kind, def, documents, docOwners, journals, journalLines, accountOwnerId, docFitsKind]);
  const txnTotal = (r: Row) => {
    const c = txnCounts.get(String(r[def.pk]));
    return c ? c.docs + c.journals : 0;
  };

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const shown = entityRows(kind)
      // eslint-disable-next-line eqeqeq
      .filter(r => showInactive || r.is_active != 0);
    const exact = shown.filter(r => !term || [r[def.nameCol], r.legal_name, r.nickname, r.alias_names, r.tags, r[def.codeCol], r.contact_name, r.email, r.phone, r.mobile, r.city]
      .some(v => String(v || '').toLowerCase().includes(term)));
    // nothing matches as typed → the names a typing slip away, rather than an empty list
    const near = term && !exact.length
      ? rankNames(term, shown.map(r => ({ item: r, name: String(r[def.nameCol] || ''), others: [r.legal_name, r.nickname, ...String(r.alias_names || '').split(',')].map(v => String(v || '')) }))).similar
      : [];
    const rows = exact.length ? exact : near;
    const toCheckFirst = (a: Row, b: Row) => Number(b.verify_status === UNVERIFIED) - Number(a.verify_status === UNVERIFIED);
    if (sortCol) {
      // A picked column: by that column alone; blanks always last, ties by name.
      if (sortCol === 'txns') {
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => toCheckFirst(a, b) || dir * (txnTotal(a) - txnTotal(b))
          || String(a[def.nameCol] || '').localeCompare(String(b[def.nameCol] || '')));
      }
      const valueOf = (r: Row): string => {
        switch (sortCol) {
          case 'name': return String(r[def.nameCol] || '');
          case 'type': return PARTY_TYPES.find(t => t.value === r.party_type)?.label || 'Other';
          case 'contact': return String(r.contact_name || '');
          case 'phone': return String(r.phone || r.mobile || '');
          case 'email': return String(r.email || '');
          case 'city': return String(r.city || '');
          case 'terms': return String(r.payment_terms || '');
          case 'currency': return String(r.default_currency || '').trim() || baseCurrency;
          // eslint-disable-next-line eqeqeq
          case 'reconciled': return r.reconcile_required == 1 ? (lastReconciled.get(String(r[def.pk]))?.date || '0000') : '';
        }
      };
      const dir = sortDir === 'asc' ? 1 : -1;
      return [...rows].sort((a, b) => {
        const first = toCheckFirst(a, b);
        if (first) return first;
        const av = valueOf(a).trim(), bv = valueOf(b).trim();
        if (!av !== !bv) return av ? -1 : 1;
        return dir * av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' })
          || String(a[def.nameCol] || '').localeCompare(String(b[def.nameCol] || ''));
      });
    }
    return rows
      // to-check entries (created from a receipt) first, then shared, then these books' own — each by name
      .sort((a, b) => toCheckFirst(a, b)
        || Number(b.owner_type === SHARED) - Number(a.owner_type === SHARED)
        || String(a[def.nameCol] || '').localeCompare(String(b[def.nameCol] || '')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRows, kind, def, search, showInactive, sortCol, sortDir, baseCurrency, lastReconciled, txnCounts]);

  // Parties named on this set of books' FILED documents that are in NONE of its three
  // lists yet, and whose document type fits the list being viewed. Only filed documents
  // count: on those the user has confirmed the party; an unfiled document's party is
  // still an auto-read guess.
  const importCandidates = useMemo(() => {
    if (!entity) return [];
    const mine = new Set<any>();
    docOwners.forEach(o => {
      // eslint-disable-next-line eqeqeq
      if (o.is_deleted != 1 && o.owner_type === entity.owner_type && o.owner_id == entity.owner_id) mine.add(Number(o.document_id));
    });
    const have = new Set<string>();
    KINDS.forEach(k => entityRows(k.key).forEach(r => rowNames(r, k.key).forEach(n => have.add(n))));
    const byName = new Map<string, { key: string; name: string; docs: number; lastDate: string; types: Set<string>; busiId: number | null }>();
    documents.forEach(d => {
      // eslint-disable-next-line eqeqeq
      if (d.is_deleted == 1 || d.status !== 'Filed' || !docFitsKind(d, kind)) return;
      const name = String(d.party_name || '').trim();
      if (!name) return;
      // eslint-disable-next-line eqeqeq
      const belongs = (d.owner_type === entity.owner_type && d.owner_id == entity.owner_id) || mine.has(Number(d.document_id));
      if (!belongs) return;
      const key = name.toLowerCase();
      if (have.has(key)) return;
      const c = byName.get(key) || { key, name, docs: 0, lastDate: '', types: new Set<string>(), busiId: null };
      c.docs++;
      const dt = String(d.date_of_document || '').slice(0, 10);
      if (dt > c.lastDate) c.lastDate = dt;
      // eslint-disable-next-line eqeqeq
      const type = docuTypes.find(t => t.docu_type_id == d.docu_type_id);
      if (type) c.types.add(type.docu_type_name || type.type_name || type.name || '');
      if (d.global_busi_id != null && c.busiId == null) c.busiId = Number(d.global_busi_id);
      byName.set(key, c);
    });
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, documents, docOwners, docuTypes, entityRows, kind, docFitsKind]);

  // ---- Vendors and customers add themselves from FILED documents ----
  // On opening this page, every party named on a filed document of each set of books that
  // is in none of that set's lists yet is added — a customer when the document type is on
  // the money-coming-in side (sales invoice, deposit …), a vendor otherwise — marked
  // 'unverified' (listed in red) with the receipt it came from, for the user to check and
  // complete. Other parties are never added this way (no document type says "bank" or
  // "landlord"), but a name already kept as one is left alone. Only filed documents: an
  // unfiled one has no owner yet, and its party is still the reader's guess.
  // Never: the account's own people / businesses, a name the user removed from a list
  // before (it would only come back), or a document that already produced an entry.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (!dataLoaded || autoRanRef.current || quickAdd || !currentUser || !accountOwnerId || !ownerEntities.length) return;
    autoRanRef.current = true;
    (async () => {
      const lc = (v: any) => String(v == null ? '' : v).trim().toLowerCase();
      const ownNames = new Set<string>();
      usersData.forEach(u => {
        // eslint-disable-next-line eqeqeq
        if (u.user_id != accountOwnerId && u.owner_id != accountOwnerId) return;
        [`${u.first_name || ''} ${u.last_name || ''}`, u.display_name, u.busi_legal_name, u.busi_operating_name].map(lc).filter(Boolean).forEach(n => ownNames.add(n));
      });
      ownerEntities.forEach(e => ownNames.add(lc(e.display_name)));
      businessesData.forEach(b => [b.busi_legal_name, b.busi_operating_name].map(lc).filter(Boolean).forEach(n => ownNames.add(n)));

      const str = (v: any, n: number) => { const t = String(v == null ? '' : v).trim(); return t ? t.slice(0, n) : null; };
      const now = NOW_ISO();
      // A document that already produced an entry never produces another — the user may
      // have renamed it (the reader took "Mastercard" off a Clearview receipt) or removed
      // it; either way the receipt is dealt with.
      const usedDocs = new Set<number>();
      KINDS.forEach(k => rowsByKind[k.key].forEach(r => {
        // eslint-disable-next-line eqeqeq
        if (r.account_owner_id == accountOwnerId && r.source_document_id != null) usedDocs.add(Number(r.source_document_id));
      }));
      const added: Record<Kind, number> = { vendor: 0, customer: 0, other: 0 };
      for (const ent of ownerEntities) {
        const mine = new Set<number>();
        docOwners.forEach(o => {
          // eslint-disable-next-line eqeqeq
          if (o.is_deleted != 1 && o.owner_type === ent.owner_type && o.owner_id == ent.owner_id) mine.add(Number(o.document_id));
        });
        // every name these books already know, in ANY of the three lists — active OR
        // removed — plus the shared ones
        const known = new Set<string>();
        KINDS.forEach(k => rowsByKind[k.key].forEach(r => {
          // eslint-disable-next-line eqeqeq
          if (r.account_owner_id != accountOwnerId) return;
          // eslint-disable-next-line eqeqeq
          if ((r.owner_type === ent.owner_type && r.owner_id == ent.owner_id) || r.owner_type === SHARED) rowNames(r, k.key).forEach(n => known.add(n));
        }));
        const found = new Map<string, { name: string; docId: number; date: string; busiId: number | null; to: Kind }>();
        documents.forEach(d => {
          // eslint-disable-next-line eqeqeq
          if (d.is_deleted == 1 || d.status !== 'Filed' || d.account_owner_id != accountOwnerId) return;
          if (usedDocs.has(Number(d.document_id))) return;
          const name = String(d.party_name || '').trim();
          const key = lc(name);
          if (!key || known.has(key) || ownNames.has(key)) return;
          // eslint-disable-next-line eqeqeq
          if (!((d.owner_type === ent.owner_type && d.owner_id == ent.owner_id) || mine.has(Number(d.document_id)))) return;
          const date = String(d.date_of_document || '').slice(0, 10);
          const cur = found.get(key);
          // the newest document names the receipt to check against — and decides the list
          if (!cur || date > cur.date) found.set(key, {
            name, docId: Number(d.document_id), date, to: sideOfDoc(d) === 'customer' ? 'customer' : 'vendor',
            busiId: d.global_busi_id != null ? Number(d.global_busi_id) : (cur ? cur.busiId : null),
          });
        });
        for (const c of Array.from(found.values())) {
          const to = KINDS.find(k => k.key === c.to)!;
          let f: Row | null = null;
          try {
            const resp = await apiClient.get<{ facts: Row | null }>('/directory/facts', { name: c.name });
            f = (resp.data && resp.data.facts) || null;
          } catch { /* the name alone is still worth adding */ }
          try {
            await addRow(to.table, {
              account_owner_id: accountOwnerId, owner_type: ent.owner_type, owner_id: ent.owner_id,
              [to.nameCol]: c.name.slice(0, 255),
              address_1: f ? str(f.address_1, 255) : null, address_2: f ? str(f.address_2, 255) : null,
              city: f ? str(f.city_name, 100) : null, state_province: f ? str(f.state_name, 100) : null,
              postal_code: f ? str(f.postal_code, 20) : null,
              phone: f ? str(f.general_phone, 30) : null, mobile: f ? str(f.general_mobile, 30) : null, fax: f ? str(f.general_fax, 30) : null,
              email: f ? str(f.general_email, 255) : null, website: f ? str(f.website, 500) : null,
              tax_id: f ? str(f.tax_id, 50) : null, tax_id_2: f ? str(f.tax_id_2, 50) : null,
              default_currency: entityBaseCurrency(ent.owner_type, ent.owner_id, usersData, businessesData) || null,
              global_busi_id: (f && f.busi_id != null ? Number(f.busi_id) : c.busiId) || null,
              verify_status: UNVERIFIED, source_document_id: c.docId,
              is_active: 1, is_deleted: 0, created_by: currentUser.user_id, created_at: now, updated_at: now,
            }, to.pk);
            added[c.to]++;
          } catch { /* e.g. the name is already taken — nothing to add */ }
        }
      }
      // Fresh rows for the lists that grew, so the new entries get their document types too.
      const rows: Record<Kind, Row[]> = { ...rowsByKind };
      for (const k of ['vendor', 'customer'] as Kind[]) {
        if (!added[k]) continue;
        const d = KINDS.find(x => x.key === k)!;
        rows[k] = await loadTable(d.table).catch(() => getTableData(d.table));
        setRowsByKind(prev => ({ ...prev, [k]: rows[k] }));
      }

      // ---- Document types add themselves too ----
      // Each kind of document FILED for a party (its Nature of Document) becomes a line in
      // that party's "Document types" block, with the date format the filing settled and
      // that document as the sample receipt. A line the user removed is not put back; a
      // line without a format or a sample gets the blank filled. Lines the user typed are
      // never changed. A date format the party prints that none of its lines holds yet
      // then gets a line of its own ("Expense Receipt (MM/DD/YY)"): one vendor's gas, till
      // and liquor receipts are the same Nature of Document but rarely print the date the
      // same way. (Filing adds these as each receipt is filed; this catches up older ones.)
      let typesChanged = false;
      for (const k of KINDS) {
        for (const r of rows[k.key]) {
          // eslint-disable-next-line eqeqeq
          if (r.account_owner_id != accountOwnerId || r.is_deleted == 1 || String(r.owner_type || '').startsWith('client_') !== (viewMode === 'clients')) continue;
          const names = new Set(rowNames(r, k.key));
          const partyId = Number(r[k.pk]);
          // what was filed for this party, newest first; a shared party takes every own book's
          const filed = documents
            .filter(d => {
              // eslint-disable-next-line eqeqeq
              if (d.is_deleted == 1 || d.status !== 'Filed' || d.account_owner_id != accountOwnerId || d.docu_type_id == null) return false;
              if (!names.has(lc(d.party_name))) return false;
              if (r.owner_type === SHARED) return true;
              // eslint-disable-next-line eqeqeq
              return (d.owner_type === r.owner_type && d.owner_id == r.owner_id)
                // eslint-disable-next-line eqeqeq
                || docOwners.some(o => o.is_deleted != 1 && o.document_id == d.document_id && o.owner_type === r.owner_type && o.owner_id == r.owner_id);
            })
            .sort((a, b) => String(b.date_of_document || '').localeCompare(String(a.date_of_document || '')) || Number(b.document_id) - Number(a.document_id));
          if (!filed.length) continue;
          // eslint-disable-next-line eqeqeq
          const lines = docTypeRows.filter(t => t.party_kind === k.key && t.party_id == partyId);
          const asType = (t: Row): PartyDocType => ({
            party_doc_type_id: Number(t.party_doc_type_id), party_kind: k.key, party_id: partyId,
            doc_type_name: String(t.doc_type_name || ''), match_words: t.match_words || '',
            date_format_id: t.date_format_id != null ? Number(t.date_format_id) : null,
          });
          // eslint-disable-next-line eqeqeq
          const known = lines.filter(t => t.is_deleted != 1).map(asType);
          // eslint-disable-next-line eqeqeq
          const removed = lines.filter(t => t.is_deleted == 1).map(asType);
          const seen = new Set<string>();
          for (const d of filed) {
            // eslint-disable-next-line eqeqeq
            const type = docuTypes.find(t => t.docu_type_id == d.docu_type_id);
            const typeName = String(type?.docu_type_name || '').trim();
            const key = lc(typeName);
            if (!key || seen.has(key)) continue;
            seen.add(key);
            // the format comes from the newest document of this kind that has one
            // eslint-disable-next-line eqeqeq
            const withFormat = filed.find(x => x.docu_type_id == d.docu_type_id && x.date_format_id != null);
            const formatId = withFormat ? Number(withFormat.date_format_id) : null;
            const sampleId = Number((withFormat || d).document_id);
            const line = lines.find(t => lc(t.doc_type_name) === key);
            try {
              if (!line) {
                await addRow('acctg_party_doc_type', {
                  account_owner_id: accountOwnerId, party_kind: k.key, party_id: partyId,
                  doc_type_name: typeName.slice(0, 100), match_words: null,
                  date_format_id: formatId, sample_document_id: sampleId, sort_order: (lines.length + seen.size) * 10,
                  is_active: 1, is_deleted: 0, created_by: currentUser.user_id, created_at: now, updated_at: now,
                }, 'party_doc_type_id');
                known.push(asType({ party_doc_type_id: 0, doc_type_name: typeName, date_format_id: formatId }));
                typesChanged = true;
              // eslint-disable-next-line eqeqeq
              } else if (line.is_deleted != 1 && ((line.date_format_id == null && formatId != null) || line.sample_document_id == null)) {
                await saveRow('acctg_party_doc_type', line.party_doc_type_id, {
                  date_format_id: line.date_format_id != null ? line.date_format_id : formatId,
                  sample_document_id: line.sample_document_id != null ? line.sample_document_id : sampleId, updated_at: now,
                }, 'party_doc_type_id');
                // eslint-disable-next-line eqeqeq
                const kn = known.find(t => t.party_doc_type_id == line.party_doc_type_id);
                if (kn && kn.date_format_id == null) kn.date_format_id = formatId;
                typesChanged = true;
              }
            } catch { /* best-effort — the party itself is fine without it */ }
          }
          // the other date formats this party's filed documents proved, oldest first
          for (const d of [...filed].reverse()) {
            if (d.date_format_id == null) continue;
            const formatId = Number(d.date_format_id);
            // eslint-disable-next-line eqeqeq
            const fmt = dateFormats.find(f => f.date_format_id == formatId);
            // eslint-disable-next-line eqeqeq
            const typeName = String(docuTypes.find(t => t.docu_type_id == d.docu_type_id)?.docu_type_name || '').trim();
            const plan = planDocTypeLearning({
              types: known, removed, docuTypeName: typeName, text: String(d.filename || ''),
              formatId, formatLabel: String(fmt?.display_pattern || fmt?.format_code || `format ${formatId}`),
            });
            if (plan.action !== 'add') continue;
            try {
              await addRow('acctg_party_doc_type', {
                account_owner_id: accountOwnerId, party_kind: k.key, party_id: partyId,
                doc_type_name: plan.name, match_words: null,
                date_format_id: formatId, sample_document_id: Number(d.document_id), sort_order: (known.length + 1) * 10,
                is_active: 1, is_deleted: 0, created_by: currentUser.user_id, created_at: now, updated_at: now,
              }, 'party_doc_type_id');
              known.push(asType({ party_doc_type_id: 0, doc_type_name: plan.name, date_format_id: formatId }));
              typesChanged = true;
            } catch { /* best-effort */ }
          }
        }
      }
      if (typesChanged) setDocTypeRows(await loadTable('acctg_party_doc_type').catch(() => getTableData('acctg_party_doc_type')));

      if (!added.vendor && !added.customer) return;
      const parts = [
        added.vendor ? `${added.vendor} new vendor${added.vendor === 1 ? '' : 's'}` : '',
        added.customer ? `${added.customer} new customer${added.customer === 1 ? '' : 's'}` : '',
      ].filter(Boolean).join(' and ');
      setMsg(`${parts} added from filed documents — shown in red until you check the details and save.`);
      setTimeout(() => setMsg(''), 12000);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, currentUser, accountOwnerId, ownerEntities]);

  const openImport = useCallback(async () => {
    if (!(await confirmDiscardChanges())) return;
    setForm(null); setEditingId(null); setErrors({}); setFormDirty(false);
    setImportPicked(new Set(importCandidates.map(c => c.key)));
    setImportOpen(true);
  }, [importCandidates]);

  // ---- Form open / close ----
  // An entry is edited right under its own row in the list, not at the top of the page
  // (a long list left the form out of sight). On Save or Cancel the list comes back to
  // that row and focus returns to its name.
  const listRef = useRef<HTMLDivElement>(null);
  const returnToIdRef = useRef<string>('');
  const rowEl = (id: string) => listRef.current?.querySelector<HTMLElement>(`tr[data-party-id="${CSS.escape(id)}"]`) || null;
  // Scroll the row to the top of the page, BELOW the bars that stay pinned there (the
  // service bar, a pinned menu): scrollIntoView alone tucks it underneath them.
  const scrollRowToTop = (tr: HTMLElement) => {
    const pinned = Array.from(document.querySelectorAll<HTMLElement>('body *')).reduce((max, el) => {
      const pos = getComputedStyle(el).position;
      if (pos !== 'sticky' && pos !== 'fixed') return max;
      const r = el.getBoundingClientRect();
      return r.top <= 1 && r.height > 0 && r.height < 200 && r.width > 300 ? Math.max(max, r.bottom) : max;
    }, 0);
    window.scrollTo({ top: window.scrollY + tr.getBoundingClientRect().top - pinned - 8, behavior: 'smooth' });
  };
  useEffect(() => {
    if (form && editingId != null) {
      // just opened: show the row with its form below it
      const t = setTimeout(() => { const tr = rowEl(String(editingId)); if (tr) scrollRowToTop(tr); }, 0);
      return () => clearTimeout(t);
    }
    if (!form && returnToIdRef.current) {
      const id = returnToIdRef.current;
      returnToIdRef.current = '';
      const t = setTimeout(() => {
        const tr = rowEl(id);
        if (!tr) return;
        tr.scrollIntoView({ block: 'center' });
        tr.querySelector<HTMLElement>('.pm-name-link')?.focus({ preventScroll: true });
        tr.classList.add('pm-row-returned');
        setTimeout(() => tr.classList.remove('pm-row-returned'), 1600);
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form == null, editingId]);

  const closeForm = useCallback(() => {
    setForm(null); setEditingId(null); setErrors({}); setLookupMsg(''); setDocTypes([]); setAttachments([]);
    setFormDirty(false);
    if (returnKeyRef.current) { setOwnerKey(returnKeyRef.current); returnKeyRef.current = ''; }
  }, []);

  const openAdd = useCallback(async () => {
    if (!(await confirmDiscardChanges())) return;
    returnToIdRef.current = ''; // a new entry has no row to come back to
    setForm({ ...EMPTY, default_currency: baseCurrency, shared: inSharedView });
    setDocTypes([]); setAttachments([]);
    setEditingId(null); setErrors({}); setLookupMsg(''); setConfirmDeleteId(null); setImportOpen(false);
    setFormDirty(false);
  }, [baseCurrency, inSharedView]);

  const openEdit = useCallback(async (r: Row, d: KindDef) => {
    if (!(await confirmDiscardChanges())) return;
    returnToIdRef.current = String(r[d.pk]);
    setForm({
      name: r[d.nameCol] || '', legal_name: r.legal_name || '', nickname: r.nickname || '', alias_names: r.alias_names || '', tags: r.tags || '',
      code: r[d.codeCol] || '', party_type: r.party_type || 'other',
      contact_name: r.contact_name || '', contact_title: r.contact_title || '',
      email: r.email || '', phone: r.phone || '', mobile: r.mobile || '', fax: r.fax || '', website: r.website || '',
      contact_2_name: r.contact_2_name || '', contact_2_phone: r.contact_2_phone || '', contact_2_email: r.contact_2_email || '',
      mail_address_1: r.mail_address_1 || '', mail_address_2: r.mail_address_2 || '', mail_city: r.mail_city || '',
      mail_state_province: r.mail_state_province || '', mail_postal_code: r.mail_postal_code || '', mail_country: r.mail_country || '',
      home_address_1: r.home_address_1 || '', home_address_2: r.home_address_2 || '', home_city: r.home_city || '',
      home_state_province: r.home_state_province || '', home_postal_code: r.home_postal_code || '', home_country: r.home_country || '', folder_id: r.folder_id != null ? String(r.folder_id) : '',
      account_number: r.account_number || '', tax_id_2: r.tax_id_2 || '',
      default_coa_id: r.default_coa_id != null ? String(r.default_coa_id) : '',
      default_payment_method_id: r.default_payment_method_id != null ? String(r.default_payment_method_id) : '',
      credit_limit: r.credit_limit != null ? String(r.credit_limit) : '',
      opening_balance: r.opening_balance != null ? String(r.opening_balance) : '',
      opening_balance_date: String(r.opening_balance_date || '').slice(0, 10),
      // eslint-disable-next-line eqeqeq
      tax_slip_required: r.tax_slip_required == 1 ? 1 : 0,
      // eslint-disable-next-line eqeqeq
      reconcile_required: r.reconcile_required == 1 ? 1 : 0,
      ownerPicked: true, moveTo: '',
      address_1: r.address_1 || '', address_2: r.address_2 || '', city: r.city || '',
      state_province: r.state_province || '', postal_code: r.postal_code || '', country: r.country || '',
      tax_id: r.tax_id || '', payment_terms: r.payment_terms || '',
      default_currency: String(r.default_currency || '').trim().toUpperCase(), notes: r.notes || '',
      // eslint-disable-next-line eqeqeq
      is_active: r.is_active == 0 ? 0 : 1,
      global_busi_id: r.global_busi_id != null ? Number(r.global_busi_id) : null,
      shared: r.owner_type === SHARED,
    });
    setDocTypes(docTypeRows
      // eslint-disable-next-line eqeqeq
      .filter(t => t.is_deleted != 1 && t.party_kind === d.key && t.party_id == r[d.pk])
      .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.party_doc_type_id) - Number(b.party_doc_type_id))
      .map(t => ({
        id: Number(t.party_doc_type_id), name: String(t.doc_type_name || ''), words: String(t.match_words || ''),
        format: t.date_format_id != null ? String(t.date_format_id) : '',
        sample: t.sample_document_id != null ? String(t.sample_document_id) : '',
        nature: t.nature_id != null ? String(t.nature_id) : '',
        coa: t.coa_id != null ? String(t.coa_id) : '',
      })));
    setAttachments(attachmentRows
      // eslint-disable-next-line eqeqeq
      .filter(a => a.is_deleted != 1 && a.party_kind === d.key && a.party_id == r[d.pk])
      .sort((a, b) => Number(a.attachment_id) - Number(b.attachment_id))
      .map(a => ({ id: Number(a.attachment_id), name: String(a.file_name || ''), path: String(a.file_path || ''), mime: String(a.mime_type || ''), size: Number(a.file_size) || 0 })));
    // An entry opened from "All … from all books" / "Shared (all books)" that belongs to
    // one set of books is edited IN those books (saving from there would make it shared);
    // closing the form goes back to the view it was opened from.
    const ownKey = `${r.owner_type}:${r.owner_id}`;
    if (r.owner_type !== SHARED && ownKey !== ownerKey) { if (!returnKeyRef.current) returnKeyRef.current = ownerKey; setOwnerKey(ownKey); }
    setEditingId(r[d.pk]); setErrors({}); setLookupMsg(''); setConfirmDeleteId(null);
    setFormDirty(false);
    // What was read off this party's receipts fills the blank fields by itself.
    const partyName = String(r[d.nameCol] || '');
    setTimeout(() => fillRef.current(true, partyName), 0);
  }, [docTypeRows, attachmentRows, ownerKey]);

  useEffect(() => {
    if (quickAdd) return;
    writeRefreshValue('parties.open', editingId == null ? null : { kind, id: editingId, returnKey: returnKeyRef.current });
  }, [editingId, kind, quickAdd]);
  useEffect(() => {
    if (!refreshOpen || !dataLoaded) return;
    setRefreshOpen(null);
    const d = KINDS.find(k => k.key === refreshOpen.kind);
    // eslint-disable-next-line eqeqeq
    const row = d && rowsByKind[d.key].find(r => r[d.pk] == refreshOpen.id && r.is_deleted != 1);
    if (!d || !row) return;          // removed since — the list shows as usual
    returnKeyRef.current = refreshOpen.returnKey || '';
    openEdit(row, d);
  }, [refreshOpen, dataLoaded, rowsByKind, openEdit]);

  // ---- Document types block ----
  const addDocType = useCallback(() => {
    setFormDirty(true);
    setDocTypes(prev => [...prev, { id: null, name: '', words: '', format: '', sample: '', nature: '', coa: '' }]);
  }, []);
  const changeDocType = useCallback((index: number, patch: Partial<DocTypeLine>) => {
    setFormDirty(true);
    setDocTypes(prev => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    setErrors(prev => ({ ...prev, docTypes: '' }));
  }, []);
  // A saved line is only marked (soft-deleted on Save); an unsaved one just goes.
  const removeDocType = useCallback((index: number) => {
    setFormDirty(true);
    setDocTypes(prev => (prev[index].id == null ? prev.filter((_, i) => i !== index) : prev.map((l, i) => (i === index ? { ...l, removed: true } : l))));
  }, []);

  const dateFormatOptions = useMemo(() => dateFormats
    // eslint-disable-next-line eqeqeq
    .filter(f => f.is_deleted != 1 && f.is_active != 0)
    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
    .map(f => ({ value: String(f.date_format_id), label: `${f.display_pattern || f.format_code}${f.example ? `   e.g. ${f.example}` : ''}` })), [dateFormats]);

  // This party's documents (by party name, legal name, nickname or an alias) — the
  // receipts a document type's sample can be picked from. Newest first.
  const partyNames = useMemo(() => (!form ? [] : Array.from(new Set([form.name, form.legal_name, form.nickname, ...form.alias_names.split(/[,;]+/)]
    .map(n => n.trim().toLowerCase()).filter(Boolean)))), [form]);
  // every live document of the account: the sample picker can search them all
  const accountDocs = useMemo(() => documents
    // eslint-disable-next-line eqeqeq
    .filter(d => d.is_deleted != 1 && d.account_owner_id == accountOwnerId), [documents, accountOwnerId]);
  const partyDocOptions = useMemo(() => {
    if (!form || !partyNames.length) return [];
    return accountDocs
      .filter(d => isPartyDoc(d, partyNames))
      .sort((a, b) => String(b.date_of_document || '').localeCompare(String(a.date_of_document || '')) || Number(b.document_id) - Number(a.document_id))
      .map(d => ({ value: String(d.document_id), label: docLabel(d) }));
  }, [form, partyNames, accountDocs]);

  // Quick add: open a new entry as soon as the books are known.
  const quickOpenedRef = useRef(false);
  useEffect(() => {
    if (!quickAdd || quickOpenedRef.current || !entity) return;
    quickOpenedRef.current = true;
    setForm({ ...EMPTY, name: quickAdd.name || '', default_currency: baseCurrency, shared: inSharedView });
  }, [quickAdd, entity, baseCurrency, inSharedView]);

  const change = useCallback(<K extends keyof PartyForm>(field: K, value: PartyForm[K]) => {
    setFormDirty(true);
    setForm(prev => (prev ? { ...prev, [field]: value } : prev));
    setErrors(prev => ({ ...prev, [field]: '' }));
  }, []);

  // ---- Guarded navigation between lists / books (guard-then-land) ----
  const switchKind = useCallback(async (k: Kind) => {
    if (!(await confirmDiscardChanges())) return;
    closeForm(); setKind(k); setSearch(''); setConfirmDeleteId(null); setMsg(''); setImportOpen(false);
  }, [closeForm]);

  const switchEntity = useCallback(async (key: string) => {
    if (key === ownerKey) return;
    // A NEW entry being typed follows the books chosen: the form stays open and now adds
    // to those books (what was typed is kept). The default account belongs to one chart,
    // so it is cleared; the currency follows the new books unless the user picked another.
    if (form && editingId == null) {
      const next = bookEntities.find(e => `${e.owner_type}:${e.owner_id}` === key);
      const nextCurrency = next ? entityBaseCurrency(next.owner_type, next.owner_id, usersData, businessesData) : '';
      setForm(prev => (prev ? {
        ...prev,
        shared: next?.owner_type === SHARED,
        default_coa_id: '',
        default_currency: !prev.default_currency || prev.default_currency === baseCurrency ? nextCurrency : prev.default_currency,
      } : prev));
      setOwnerKey(key); setErrors({}); setConfirmDeleteId(null); setMsg(''); setImportOpen(false);
      return;
    }
    if (!(await confirmDiscardChanges())) return;
    closeForm(); setOwnerKey(key); setConfirmDeleteId(null); setMsg(''); setImportOpen(false);
  }, [ownerKey, closeForm, form, editingId, bookEntities, usersData, businessesData, baseCurrency]);

  const reload = useCallback(async (k: Kind) => {
    const d = KINDS.find(x => x.key === k)!;
    const rows = await loadTable(d.table).catch(() => getTableData(d.table));
    setRowsByKind(prev => ({ ...prev, [k]: rows }));
  }, []);

  const notifyOwner = useCallback((title: string, message: string) => {
    if (!currentUser || !accountOwnerId) return;
    addNotification({
      account_owner_id: accountOwnerId, type: 'bookkeeping', title, message,
      actor_user_id: currentUser.user_id, actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
    });
  }, [currentUser, accountOwnerId]);

  // ---- An entry created from a receipt is really an existing party (a misread name) ----
  // "Clearview Auto Ltd." read off a Clearview Agro receipt: the read name becomes an alias
  // of the existing entry (so the next receipt is recognised), every document of the account
  // carrying that read name is pointed at the existing party, and the red entry is removed
  // (soft delete — its receipt stays "used", so it is not added again).
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [merging, setMerging] = useState(false);
  const mergeIntoExisting = useCallback(async () => {
    // eslint-disable-next-line eqeqeq
    const from = editingId != null ? rowsByKind[kind].find(r => r[def.pk] == editingId) : null;
    // eslint-disable-next-line eqeqeq
    const into = rowsByKind[kind].find(r => String(r[def.pk]) === mergeTargetId);
    if (!from || !into || merging) return;
    const readName = String(from[def.nameCol] || '').trim();
    const intoName = String(into[def.nameCol] || '').trim();
    if (!readName || !intoName) return;
    setMerging(true);
    try {
      const aliases = new Map<string, string>();
      [...String(into.alias_names || '').split(/[,;]+/), readName, String(from.legal_name || '')]
        .map(a => a.trim()).filter(a => a && a.toLowerCase() !== intoName.toLowerCase())
        .forEach(a => { if (!aliases.has(a.toLowerCase())) aliases.set(a.toLowerCase(), a); });
      const now = NOW_ISO();
      await saveRow(def.table, into[def.pk], { alias_names: Array.from(aliases.values()).join(', ') || null, updated_at: now }, def.pk);
      const misnamed = documents.filter(d =>
        // eslint-disable-next-line eqeqeq
        d.account_owner_id == accountOwnerId && d.is_deleted != 1 && String(d.party_name || '').trim().toLowerCase() === readName.toLowerCase());
      for (const d of misnamed) {
        try { await saveRow('dms_documents', d.document_id, { party_name: intoName }, 'document_id'); } catch { /* the alias still recognises it */ }
      }
      const ids = new Set(misnamed.map(d => Number(d.document_id)));
      setDocuments(prev => prev.map(d => (ids.has(Number(d.document_id)) ? { ...d, party_name: intoName } : d)));
      await saveRow(def.table, from[def.pk], { is_deleted: 1, is_active: 0, updated_at: now }, def.pk);
      await reload(kind);
      setFormDirty(false);
      closeForm();
      setMergeTargetId('');
      setMsg(`"${readName}" is now an alias of ${intoName}${misnamed.length ? `; ${misnamed.length} document${misnamed.length === 1 ? '' : 's'} now name ${intoName}` : ''}.`);
      setTimeout(() => setMsg(''), 12000);
      notifyOwner(`${def.singular} merged`, `"${readName}" (read from a receipt) was merged into ${intoName} as an alias.`);
    } catch (err: any) {
      setMsg(`Could not merge: ${err?.message || 'the server rejected the change'}.`);
    } finally {
      setMerging(false);
    }
  }, [editingId, rowsByKind, kind, def, mergeTargetId, merging, documents, accountOwnerId, reload, closeForm, notifyOwner]);

  // ---- Save ----
  const handleSave = useCallback(async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    if (!form || !entity || !currentUser || busy) return;
    const name = form.name.trim();
    const errs: Record<string, string> = {};
    if (!name) errs.name = `${def.singular} name is required`;
    if (form.email.trim() && !isEmail(form.email.trim())) errs.email = 'Invalid email format';
    if (form.contact_2_email.trim() && !isEmail(form.contact_2_email.trim())) errs.contact_2_email = 'Invalid email format';
    const money = (v: string) => (v.trim() === '' ? null : Number(v.replace(/,/g, '')));
    if (form.credit_limit.trim() && !Number.isFinite(money(form.credit_limit))) errs.credit_limit = 'Enter a number';
    if (form.opening_balance.trim() && !Number.isFinite(money(form.opening_balance))) errs.opening_balance = 'Enter a number';
    // "All … from all books" only lists; it owns nothing. A new entry made there must say
    // whose books it belongs to (one set, or shared across all of them).
    if (inAllView && editingId == null && !form.ownerPicked) errs.owner = `Choose the ${def.singular.toLowerCase()} account owner — whose books this belongs to`;
    // Where the entry lives: this set of books, or shared across all of the account's own books.
    // An existing entry being moved to one set of books is no longer shared, even when it
    // was opened from the Shared / All view.
    const toShared = canShare && !(editingId != null && form.moveTo) && (form.shared || inSharedView);
    // Editing can also move the entry to another set of the account's own books.
    // eslint-disable-next-line eqeqeq
    const moveTo = !toShared && editingId != null && form.moveTo ? bookEntities.find(e => `${e.owner_type}:${e.owner_id}` === form.moveTo && e.owner_type !== SHARED && e.owner_type !== ALL) || null : null;
    const target = toShared ? { owner_type: SHARED, owner_id: accountOwnerId }
      : moveTo ? { owner_type: moveTo.owner_type, owner_id: moveTo.owner_id }
      : { owner_type: entity.owner_type, owner_id: entity.owner_id };
    // A shared entry appears in every set of books, so its name must be free in ALL of
    // them; an ordinary entry only has to be free among what its own books list.
    const scope = toShared ? ownBooksRows(kind)
      // eslint-disable-next-line eqeqeq
      : moveTo ? ownBooksRows(kind).filter(r => r.owner_type === SHARED || (r.owner_type === moveTo.owner_type && r.owner_id == moveTo.owner_id))
      : entityRows(kind);
    const clash = scope.find(r =>
      // eslint-disable-next-line eqeqeq
      String(r[def.nameCol] || '').trim().toLowerCase() === name.toLowerCase() && r[def.pk] != editingId);
    // The nickname names files, so two parties in the same list must not share one.
    const nick = form.nickname.trim();
    if (nick && scope.some(r =>
      // eslint-disable-next-line eqeqeq
      String(r.nickname || '').trim().toLowerCase() === nick.toLowerCase() && r[def.pk] != editingId)) {
      errs.nickname = 'Another entry in this list already uses this nickname / short name';
    }
    if (name && clash) {
      const where = clash.owner_type === SHARED ? 'as a shared entry'
        // eslint-disable-next-line eqeqeq
        : `for ${bookEntities.find(e => e.owner_type === clash.owner_type && e.owner_id == clash.owner_id)?.display_name || 'another set of books'}`;
      errs.name = `A ${def.singular.toLowerCase()} with this name already exists ${where}`
        + (toShared && clash.owner_type !== SHARED ? ' — open that one and make it shared instead.' : '.');
    }
    // Document types: a line needs a name, and one party cannot have the same name twice.
    const liveTypes = docTypes.filter(l => !l.removed && (l.name.trim() || l.words.trim() || l.format || l.sample || l.nature || l.coa));
    const typeNames = liveTypes.map(l => l.name.trim().toLowerCase());
    if (liveTypes.some(l => !l.name.trim())) errs.docTypes = 'Give each document type a name (or remove the empty line).';
    else if (new Set(typeNames).size !== typeNames.length) errs.docTypes = 'Two document types have the same name.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const now = NOW_ISO();
    const oneLine = (p: AddrPrefix) => (['address_1', 'address_2', 'city', 'state_province', 'postal_code', 'country'] as const)
      .map(n => String((form as any)[`${p}${n}`] || '').trim()).filter(Boolean).join(', ');
    const fields: Row = {
      [def.nameCol]: name,
      legal_name: form.legal_name.trim() || null,
      nickname: nick || null,
      // tidy the list: trimmed, no blanks, no repeats, never the party's own name
      alias_names: Array.from(new Map(form.alias_names.split(/[,;]+/).map(a => a.trim()).filter(a => a && a.toLowerCase() !== name.toLowerCase()).map(a => [a.toLowerCase(), a] as [string, string])).values()).join(', ') || null,
      tags: joinTags(parseTags(form.tags)),
      [def.codeCol]: form.code.trim() || null,
      contact_name: form.contact_name.trim() || null,
      contact_title: form.contact_title.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      mobile: form.mobile.trim() || null,
      fax: form.fax.trim() || null,
      website: form.website.trim() || null,
      contact_2_name: form.contact_2_name.trim() || null,
      contact_2_phone: form.contact_2_phone.trim() || null,
      contact_2_email: form.contact_2_email.trim() || null,
      // the old one-box columns keep the address as one line, for anything still reading them
      contact_home_address: oneLine('home_') || null,
      alt_address: oneLine('mail_') || null,
      folder_id: form.folder_id ? Number(form.folder_id) : null,
      account_number: form.account_number.trim() || null,
      tax_id_2: form.tax_id_2.trim() || null,
      // a default account belongs to ONE chart, so a shared entry carries none
      // (and one moved to other books leaves the old chart's account behind)
      default_coa_id: !toShared && !moveTo && form.default_coa_id ? Number(form.default_coa_id) : null,
      default_payment_method_id: form.default_payment_method_id ? Number(form.default_payment_method_id) : null,
      credit_limit: money(form.credit_limit),
      // an opening-balances journal entry naming the party sets the figure; else what was typed
      opening_balance: journalOpening ? journalOpening.amount : money(form.opening_balance),
      opening_balance_date: journalOpening ? journalOpening.date || null : form.opening_balance_date || null,
      tax_slip_required: form.tax_slip_required ? 1 : 0,
      reconcile_required: form.reconcile_required ? 1 : 0,
      address_1: form.address_1.trim() || null,
      address_2: form.address_2.trim() || null,
      city: form.city.trim() || null,
      state_province: form.state_province.trim() || null,
      postal_code: form.postal_code.trim() || null,
      country: form.country || null,
      tax_id: form.tax_id.trim() || null,
      payment_terms: form.payment_terms.trim() || null,
      default_currency: form.default_currency || null,
      notes: form.notes.trim() || null,
      is_active: form.is_active ? 1 : 0,
      verify_status: 'verified',
      ...Object.fromEntries((['mail_', 'home_'] as const).flatMap(p => ADDR_PARTS.map(n => [`${p}${n}`, String((form as any)[`${p}${n}`] || '').trim() || null]))),          // a person saved it — checked
      updated_at: now,
    };
    if (kind === 'other') fields.party_type = form.party_type || 'other';
    if (form.global_busi_id) fields.global_busi_id = form.global_busi_id;

    setBusy(true);
    let partyId: number | string | null = null;
    try {
      if (editingId != null) {
        // Saving can also move an entry between "these books only" and "shared".
        await saveRow(def.table, editingId, { ...fields, owner_type: target.owner_type, owner_id: target.owner_id }, def.pk);
        partyId = editingId;
        // An entry created from a receipt and now renamed: the name the reader took off
        // that receipt was wrong, so the receipt's party is corrected with it.
        // eslint-disable-next-line eqeqeq
        const before = rowsByKind[kind].find(r => r[def.pk] == editingId);
        const oldName = String(before?.[def.nameCol] || '').trim();
        if (before && before.source_document_id != null && oldName && oldName.toLowerCase() !== name.toLowerCase()) {
          // eslint-disable-next-line eqeqeq
          const src = documents.find(d => d.document_id == before.source_document_id);
          if (src && String(src.party_name || '').trim().toLowerCase() === oldName.toLowerCase()) {
            try {
              await saveRow('dms_documents', src.document_id, { party_name: name }, 'document_id');
              // eslint-disable-next-line eqeqeq
              setDocuments(prev => prev.map(d => (d.document_id == src.document_id ? { ...d, party_name: name } : d)));
            } catch { /* the vendor is saved; the receipt keeps its old party name */ }
          }
        }
        notifyOwner(`${def.singular} updated`, moveTo
          ? `${name} was moved from ${entity.display_name} to ${moveTo.display_name}.`
          : `${name} was updated for ${entity.display_name}.`);
      } else {
        // A removed party with the same name still holds that name in the table — bring it
        // back instead of failing on the unique name.
        const removed = rowsByKind[kind].find(r =>
          // eslint-disable-next-line eqeqeq
          r.account_owner_id == accountOwnerId && r.owner_type === target.owner_type && r.owner_id == target.owner_id &&
          // eslint-disable-next-line eqeqeq
          r.is_deleted == 1 && String(r[def.nameCol] || '').trim().toLowerCase() === name.toLowerCase());
        if (removed) {
          await saveRow(def.table, removed[def.pk], { ...fields, is_deleted: 0 }, def.pk);
          partyId = removed[def.pk];
        } else {
          const added = await addRow(def.table, {
            account_owner_id: accountOwnerId, owner_type: target.owner_type, owner_id: target.owner_id,
            ...fields, is_deleted: 0, created_by: currentUser.user_id, created_at: now,
          }, def.pk);
          partyId = added ? added[def.pk] : null;
        }
        notifyOwner(`${def.singular} added`, `${name} was added for ${entity.display_name}.`);
      }
      // The party's document types (each with its date layout) — a child table, so they
      // are written once the party itself has an id.
      if (partyId != null) {
        let order = 0;
        for (const l of docTypes) {
          if (l.removed) {
            if (l.id != null) await saveRow('acctg_party_doc_type', l.id, { is_deleted: 1, updated_at: now }, 'party_doc_type_id');
            continue;
          }
          if (!l.name.trim()) continue;
          const line: Row = {
            doc_type_name: l.name.trim().slice(0, 100), match_words: l.words.trim() || null,
            date_format_id: l.format ? Number(l.format) : null, sample_document_id: l.sample ? Number(l.sample) : null,
            nature_id: l.nature ? Number(l.nature) : null,
            coa_id: l.coa && !toShared && !moveTo ? Number(l.coa) : null,   // an account belongs to one set of books
            sort_order: (order += 10), updated_at: now,
          };
          if (l.id != null) await saveRow('acctg_party_doc_type', l.id, line, 'party_doc_type_id');
          else await addRow('acctg_party_doc_type', {
            account_owner_id: accountOwnerId, party_kind: kind, party_id: Number(partyId), ...line,
            is_active: 1, is_deleted: 0, created_by: currentUser.user_id, created_at: now,
          }, 'party_doc_type_id');
        }
        if (docTypes.length) setDocTypeRows(await loadTable('acctg_party_doc_type').catch(() => getTableData('acctg_party_doc_type')));
        // Attachments: the bytes are already in the blob store; the rows are written now.
        for (const a of attachments) {
          if (a.removed) {
            if (a.id != null) await saveRow('acctg_party_attachment', a.id, { is_deleted: 1, updated_at: now }, 'attachment_id');
          } else if (a.id == null) {
            await addRow('acctg_party_attachment', {
              account_owner_id: accountOwnerId, party_kind: kind, party_id: Number(partyId),
              file_name: a.name, file_path: a.path, mime_type: a.mime || null, file_size: a.size || null,
              is_active: 1, is_deleted: 0, created_by: currentUser.user_id, created_at: now, updated_at: now,
            }, 'attachment_id');
          }
        }
        if (attachments.length) setAttachmentRows(await loadTable('acctg_party_attachment').catch(() => getTableData('acctg_party_attachment')));
      }
      // tags new on this entry join / rank up in the account's tag dictionary
      // eslint-disable-next-line eqeqeq
      recordTagUse(fields.tags, editingId != null ? rowsByKind[kind].find(r => r[def.pk] == editingId)?.tags : null);
      await reload(kind);
      setFormDirty(false);
      if (quickAdd) { quickAdd.onSaved(kind, name); return; }
      setForm(null); setEditingId(null); setErrors({}); setLookupMsg(''); setDocTypes([]); setAttachments([]);
      if (returnKeyRef.current) { setOwnerKey(returnKeyRef.current); returnKeyRef.current = ''; }
      setMsg(moveTo ? `${def.singular} saved and moved to ${moveTo.display_name}.` : `${def.singular} saved.`);
      setTimeout(() => setMsg(''), 3000);
    } catch (err: any) {
      setErrors({ name: err?.message || 'Could not save — please try again.' });
    } finally {
      setBusy(false);
    }
  }, [form, entity, currentUser, busy, def, kind, editingId, entityRows, ownBooksRows, rowsByKind, accountOwnerId, notifyOwner, reload, canShare, inSharedView, inAllView, bookEntities, quickAdd, docTypes, documents, attachments, journalOpening]);

  // "Save & continue" in the unsaved-changes dialog saves the open form.
  useEffect(() => {
    if (!form) return;
    registerSaveHandler(submitSaver(handleSave));
    return () => registerSaveHandler(null);
  }, [form, handleSave]);

  // Ctrl+S saves the open form — here and in the Add Vendor / Customer / Party popup of
  // Process Docs (owner, 2026-10-17). Never the browser's "Save page".
  useEffect(() => {
    if (!form) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [form, handleSave]);

  // ---- Remove (soft-delete) ----
  const confirmDelete = useCallback(async (r: Row) => {
    if (busy || !entity) return;
    setBusy(true);
    try {
      await saveRow(def.table, r[def.pk], { is_deleted: 1, updated_at: NOW_ISO() }, def.pk);
      notifyOwner(`${def.singular} removed`, `${r[def.nameCol]} was removed from ${entity.display_name}.`);
      await reload(kind);
      // eslint-disable-next-line eqeqeq
      if (editingId == r[def.pk]) closeForm();
      setMsg(`${def.singular} removed.`);
      setTimeout(() => setMsg(''), 3000);
    } finally {
      setBusy(false); setConfirmDeleteId(null);
    }
  }, [busy, entity, def, kind, editingId, notifyOwner, reload, closeForm]);

  // ---- Add the picked parties to the list being viewed, with whatever was captured off their receipts ----
  const runImport = useCallback(async () => {
    if (busy || !entity || !currentUser) return;
    const picked = importCandidates.filter(c => importPicked.has(c.key));
    if (!picked.length) return;
    setBusy(true);
    let added = 0, withDetails = 0, failed = 0;
    const now = NOW_ISO();
    const str = (v: any, n: number) => { const t = String(v == null ? '' : v).trim(); return t ? t.slice(0, n) : null; };
    for (const c of picked) {
      let f: Row | null = null;
      try {
        const resp = await apiClient.get<{ facts: Row | null }>('/directory/facts', { name: c.name });
        f = (resp.data && resp.data.facts) || null;
      } catch { /* no details available — the name alone is still worth adding */ }
      const fields: Row = {
        [def.nameCol]: c.name.slice(0, 255),
        address_1: f ? str(f.address_1, 255) : null,
        address_2: f ? str(f.address_2, 255) : null,
        city: f ? str(f.city_name, 100) : null,
        state_province: f ? str(f.state_name, 100) : null,
        postal_code: f ? str(f.postal_code, 20) : null,
        phone: f ? str(f.general_phone, 30) : null,
        mobile: f ? str(f.general_mobile, 30) : null,
        fax: f ? str(f.general_fax, 30) : null,
        tax_id_2: f ? str(f.tax_id_2, 50) : null,
        email: f ? str(f.general_email, 255) : null,
        website: f ? str(f.website, 500) : null,
        tax_id: f ? str(f.tax_id, 50) : null,
        default_currency: baseCurrency || null,
        global_busi_id: (f && f.busi_id != null ? Number(f.busi_id) : c.busiId) || null,
        is_active: 1, is_deleted: 0, updated_at: now,
      };
      try {
        const removed = rowsByKind[kind].find(r =>
          // eslint-disable-next-line eqeqeq
          r.account_owner_id == accountOwnerId && r.owner_type === entity.owner_type && r.owner_id == entity.owner_id &&
          // eslint-disable-next-line eqeqeq
          r.is_deleted == 1 && String(r[def.nameCol] || '').trim().toLowerCase() === c.key);
        if (removed) await saveRow(def.table, removed[def.pk], fields, def.pk);
        else await addRow(def.table, {
          account_owner_id: accountOwnerId, owner_type: entity.owner_type, owner_id: entity.owner_id,
          ...fields, ...(kind === 'other' ? { party_type: 'other' } : {}), created_by: currentUser.user_id, created_at: now,
        }, def.pk);
        added++;
        if (fields.address_1 || fields.phone || fields.email) withDetails++;
      } catch { failed++; }
    }
    await reload(kind);
    const what = (added === 1 ? def.singular : def.plural).toLowerCase();
    if (added) notifyOwner(`${def.plural} added`, `${added} ${what} added for ${entity.display_name} from filed documents.`);
    setBusy(false); setImportOpen(false);
    setMsg(`Added ${added} ${what} from filed documents`
      + (added ? ` (${withDetails} with contact details)` : '') + (failed ? ` · ${failed} could not be added` : '') + '.');
    setTimeout(() => setMsg(''), 6000);
  }, [busy, entity, currentUser, importCandidates, importPicked, baseCurrency, rowsByKind, accountOwnerId, reload, notifyOwner, kind, def]);

  // ---- Fill blanks from the details captured off receipts (any of the three lists) ----
  // `auto` = run by itself when an entry is opened: quiet unless it filled something.
  const fillFromReceipts = useCallback(async (auto?: boolean, nameArg?: string) => {
    const partyName = String(nameArg != null ? nameArg : formRef.current?.name || '').trim();
    if (!partyName) { if (!auto) setLookupMsg('Type the party name first.'); return; }
    if (!auto) setLookupMsg('Looking up…');
    try {
      const resp = await apiClient.get<{ facts: Row | null }>('/directory/facts', { name: partyName });
      const f = resp.data && resp.data.facts;
      if (!f) { if (!auto) setLookupMsg('Nothing captured from receipts for this name yet.'); return; }
      const prev = formRef.current;
      // the form was closed, or another entry opened, while the lookup was running
      if (!prev || (auto && prev.name.trim() !== partyName)) return;
      let filled = 0;
      const take = (cur: string, val: any) => { const v = String(val == null ? '' : val).trim(); if (!cur.trim() && v) { filled++; return v; } return cur; };
      setForm({
        ...prev,
        address_1: take(prev.address_1, f.address_1),
        address_2: take(prev.address_2, f.address_2),
        city: take(prev.city, f.city_name),
        state_province: take(prev.state_province, f.state_name),
        postal_code: take(prev.postal_code, f.postal_code),
        phone: take(prev.phone, f.general_phone),
        mobile: take(prev.mobile, f.general_mobile),
        fax: take(prev.fax, f.general_fax),
        tax_id_2: take(prev.tax_id_2, f.tax_id_2),
        email: take(prev.email, f.general_email),
        website: take(prev.website, f.website),
        tax_id: take(prev.tax_id, f.tax_id),
        global_busi_id: prev.global_busi_id || (f.busi_id != null ? Number(f.busi_id) : null),
      });
      // Only a fill the user asked for counts as an edit: the one that runs by itself
      // on opening an entry must not raise "unsaved changes" when the user just leaves.
      if (filled && !auto) setFormDirty(true);
      if (auto && !filled) return;
      setLookupMsg(filled ? `Filled ${filled} blank field${filled === 1 ? '' : 's'} from “${f.canonical_name}”. Review, then Save to keep ${filled === 1 ? 'it' : 'them'}.` : `Found “${f.canonical_name}”, but it has nothing new to add.`);
    } catch {
      if (!auto) setLookupMsg('Lookup is unavailable right now.');
    }
  }, []);
  fillRef.current = fillFromReceipts;
  formRef.current = form;

  // ---- Render ----
  if (!dataLoaded) return <div className="pm-tab"><div className="pm-empty">Loading…</div></div>;
  if (!ownerEntities.length) {
    return (
      <div className="pm-tab">
        <div className="pm-empty">
          {viewMode === 'clients' ? 'Select a client to manage their vendors, customers and other parties.' : 'No books found for this account yet.'}
        </div>
      </div>
    );
  }

  const text = (field: Exclude<keyof PartyForm, 'shared' | 'global_busi_id' | 'is_active' | 'tax_slip_required' | 'reconcile_required' | 'ownerPicked'>, label: string, opts: { required?: boolean; type?: string; placeholder?: string; list?: string; maxLength?: number; hint?: string; tip?: string } = {}) => (
    <label className="pm-field">
      <span className="pm-label">{label}{opts.required && <span className="pm-required">*</span>}</span>
      <PartyTip text={[opts.hint, opts.tip].filter(Boolean).join(' ')} />
      <input
        className={`pm-input ${errors[field] ? 'pm-input-error' : ''}`}
        type={opts.type || 'text'}
        value={String(form![field] ?? '')}
        placeholder={opts.placeholder}
        list={opts.list}
        maxLength={opts.maxLength}
        onChange={e => change(field, e.target.value as any)}
      />
      {errors[field] && <span className="pm-error">{errors[field]}</span>}
    </label>
  );

  function renderForm() {
    if (!form) return null;
    // eslint-disable-next-line eqeqeq
    const editingRow = editingId != null ? rowsByKind[kind].find(r => r[def.pk] == editingId) || null : null;
    // A new entry made from "All … from all books" (a view, not an owner) must be given one.
    const pickOwner = inAllView && editingId == null;
    // "+" sits in front of the first document type (no separate button row).
    const firstDocType = docTypes.findIndex(l => !l.removed);
    const addDocTypeBtn = (
      <button type="button" className="pm-icon-btn pm-doctype-plus" title="Add document type" aria-label="Add document type" onClick={addDocType}>
        <FiPlus size={18} />
      </button>
    );
    // One address block — the same fields, in the same order, for the physical, mailing
    // and home addresses: lines, then Country (it decides the list that follows),
    // State / Province, City, Postal code.
    const addressBlock = (p: AddrPrefix, of: string) => {
      const key = (n: typeof ADDR_PARTS[number]) => `${p}${n}` as any;
      const val = (n: typeof ADDR_PARTS[number]) => String((form as any)[key(n)] || '');
      const country = val('country');
      const states = statesOf(country);
      const stateNow = val('state_province').trim();
      const stateMatch = states.find(x => x.name.toLowerCase() === stateNow.toLowerCase() || (x.code && x.code.toLowerCase() === stateNow.toLowerCase()));
      const stateLabel = Array.from(new Set(states.map(x => x.type))).slice(0, 3).join(' / ') || 'State / Province';
      return (
        <div className="pm-grid">
          <div className="pm-span-2">{text(key('address_1'), 'Address line 1', { maxLength: 255, tip: `Street address — ${of}.` })}</div>
          <div className="pm-span-2">{text(key('address_2'), 'Address line 2', { maxLength: 255, tip: 'Unit, suite or building, if any.' })}</div>
          <label className="pm-field">
            <span className="pm-label">Country</span>
            <PartyTip text="Choose the country first — the next field then lists its provinces, territories or states." />
            <select className="pm-input" value={country} onChange={e => change(key('country'), e.target.value as any)}>
              <option value="">Select country</option>
              {country && !countries.includes(country) && <option value={country}>{country}</option>}
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          {states.length === 0 ? text(key('state_province'), 'State / Province', { maxLength: 100, tip: country ? 'No list for this country yet — type it.' : 'Choose the country for a list.' }) : (
            <label className="pm-field">
              <span className="pm-label">{stateLabel}</span>
              <PartyTip text={`The ${stateLabel.toLowerCase()}, from the list for ${country}.`} />
              <select className="pm-input" value={stateMatch ? stateMatch.name : stateNow} onChange={e => change(key('state_province'), e.target.value as any)}>
                <option value="">Select…</option>
                {stateNow && !stateMatch && <option value={stateNow}>{stateNow}</option>}
                {states.map(x => <option key={x.name} value={x.name}>{x.name}{x.code ? ` (${x.code})` : ''}</option>)}
              </select>
            </label>
          )}
          {text(key('city'), 'City', { maxLength: 100, tip: `City or town — ${of}.` })}
          {text(key('postal_code'), 'Zip / Postal code', { maxLength: 20, tip: `Postal or ZIP code — ${of}.` })}
        </div>
      );
    };
    // "Same as the physical address": copies its six fields into the mailing / home block.
    const copyPhysical = (p: 'mail_' | 'home_') => {
      setFormDirty(true);
      setForm(prev => (prev ? { ...prev, ...Object.fromEntries(ADDR_PARTS.map(n => [`${p}${n}`, (prev as any)[n] || ''])) } as PartyForm : prev));
    };
    return (
        <form className="pm-form" onSubmit={handleSave}>

          <div className="pm-form-head">
            <span className="pm-form-title">{editingId != null ? `Edit ${def.singular}` : `New ${def.singular}`} — {entity?.display_name}</span>
            <button type="button" className="pm-icon-btn" title="Close" onClick={leaveForm}>
              <FiX size={18} />
            </button>
          </div>

          {editingRow && editingRow.verify_status === UNVERIFIED && (
            <div className="pm-verify-note" role="status">
              <span>This {def.singular.toLowerCase()} was created from a receipt. Check the name and complete the details, then <strong>Save</strong> to confirm it.</span>
              {editingRow.source_document_id != null && (
                <button type="button" className="pm-link-btn" onClick={() => setViewDocId(Number(editingRow.source_document_id))}>
                  <FiFileText size={14} /> View receipt
                </button>
              )}
              <div className="pm-merge">
                <span>Name misread? It is the same as</span>
                <SearchableDropdown
                  wrapperClassName="pm-merge-pick"
                  options={ownBooksRows(kind)
                    // eslint-disable-next-line eqeqeq
                    .filter(r => r[def.pk] != editingRow[def.pk] && r.verify_status !== UNVERIFIED && r.is_deleted != 1)
                    .map(r => ({ value: String(r[def.pk]), label: String(r[def.nameCol] || ''), keywords: [r.legal_name, r.nickname, r.alias_names].filter(Boolean).join(' ') }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  value={mergeTargetId}
                  onChange={setMergeTargetId}
                  placeholder={`Pick an existing ${def.singular.toLowerCase()}…`}
                />
                <button type="button" className="pm-btn" disabled={!mergeTargetId || merging} onClick={mergeIntoExisting}
                  title={`Adds "${String(editingRow[def.nameCol] || '')}" as an alias of the picked ${def.singular.toLowerCase()}, points its documents there and removes this entry`}>
                  {merging ? 'Merging…' : 'Merge into it'}
                </button>
              </div>
            </div>
          )}
          <div className="pm-grid">
            <div className="pm-span-2">
              {text('name', 'Party Name to show in Filing & Bookkeeping', { required: true, maxLength: 255, tip: 'The name shown wherever this party is picked or listed. Keep it short and recognisable.' })}
            </div>
            <div className="pm-span-2">
              {text('legal_name', `Legal Name of ${def.singular}`, { maxLength: 255, tip: 'Full legal name to use in mailing & cheques' })}
            </div>
            <div className="pm-span-2">
              {text('alias_names', 'Alias Names', {
                placeholder: 'Other names, separated by commas',
                hint: 'A document under any of these names is recognised as this party.',
                tip: 'Other names this party goes by. Example, Bumper to Bumper is also known as HB Agro Sales',
              })}
            </div>
            <div className="pm-field pm-span-2">
              <span className="pm-label">Tags</span>
              <PartyTip text="Labels to find this party by in any search — e.g. rental, 118 1SNW, farm. Start typing to pick a tag used anywhere in your account; a new one is created as you type it." />
              <TagInput value={form.tags} onChange={v => change('tags', v)} ariaLabel="Tags" placeholder="Type a tag…" />
            </div>
            <div className="pm-span-2">{text('website', 'Website', { maxLength: 500, placeholder: 'www.example.com', tip: 'The party’s web address, as printed on its documents.' })}</div>
            {canShare && (
              <label className="pm-field pm-span-2">
                <span className="pm-label">{def.singular} account owner{pickOwner && <span className="pm-required">*</span>}</span>
                <PartyTip text={`Whose books this ${def.singular.toLowerCase()} belongs to. A shared ${def.singular.toLowerCase()} is entered once and listed in every set of your own books.`} />
                <select
                  className={`pm-input ${errors.owner ? 'pm-input-error' : ''}`}
                  value={pickOwner ? (form.ownerPicked ? 'shared' : '') : form.moveTo || (form.shared || inSharedView ? 'shared' : 'books')}
                  // locked only for a NEW entry in the Shared view (it can only be shared there);
                  // an existing shared entry can be moved into one set of books
                  disabled={inSharedView && !pickOwner && editingId == null}
                  onChange={e => {
                    const v = e.target.value;
                    setErrors(prev => ({ ...prev, owner: '' }));
                    if (v === '') { setFormDirty(true); setForm(prev => (prev ? { ...prev, ownerPicked: false } : prev)); }
                    else if (v === 'shared' || v === 'books') { setFormDirty(true); setForm(prev => (prev ? { ...prev, shared: v === 'shared', ownerPicked: true, moveTo: '' } : prev)); }
                    else if (!pickOwner) {
                      // editing: move the entry to another set of books when it is saved
                      setFormDirty(true);
                      setForm(prev => (prev ? { ...prev, shared: false, moveTo: v } : prev));
                    } else {
                      // one set of books: the entry is made IN those books; the list goes back to "All …" afterwards
                      if (!returnKeyRef.current) returnKeyRef.current = ownerKey;
                      setFormDirty(true);
                      switchEntity(v);
                    }
                  }}
                >
                  {pickOwner && <option value="">Select whose books…</option>}
                  {!inSharedView && <option value="books">{entity?.display_name} only</option>}
                  <option value="shared">Shared — all of your books</option>
                  {pickOwner && ownerEntities.map(e => (
                    <option key={`${e.owner_type}:${e.owner_id}`} value={`${e.owner_type}:${e.owner_id}`}>{e.display_name} only</option>
                  ))}
                  {/* editing an entry of one set of books: it can be moved to another set */}
                  {!pickOwner && editingId != null && ownerEntities
                    .filter(e => `${e.owner_type}:${e.owner_id}` !== ownerKey)
                    .map(e => (
                      <option key={`${e.owner_type}:${e.owner_id}`} value={`${e.owner_type}:${e.owner_id}`}>{e.display_name} only</option>
                    ))}
                </select>
                {form.moveTo && !form.shared && !pickOwner && (
                  <span className="pm-hint">
                    Saving moves this {def.singular.toLowerCase()} to the books of {ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === form.moveTo)?.display_name}.
                    {form.default_coa_id ? ' Its default account belongs to this set of books and is cleared.' : ''}
                  </span>
                )}
                {errors.owner && <span className="pm-error">{errors.owner}</span>}
              </label>
            )}
            {text('nickname', 'Nickname / Short name', { maxLength: 30, hint: 'Short name used when naming files.', tip: 'Example, Bumper 2 Bumper can be nicknamed as B2B' })}
            {text('code', 'Short code', { placeholder: 'Optional reference', maxLength: 50, tip: 'Your own reference for this party, e.g. a supplier number. Optional.' })}
            {kind === 'other' && (
              <label className="pm-field">
                <span className="pm-label">Type</span>
                <PartyTip text="What kind of party this is — bank, tax authority, shareholder, landlord and so on." />
                <select className="pm-input" value={form.party_type} onChange={e => change('party_type', e.target.value)}>
                  {PARTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
            )}
            <label className={`pm-field ${kind === 'other' ? '' : 'pm-span-2'}`}>
              <span className="pm-label">Related folder</span>
              <PartyTip text="Where this party’s documents are filed. Filing pre-selects it when a document names this party." />
              <SearchableDropdown
                options={folderOptions}
                value={form.folder_id}
                onChange={v => change('folder_id', v)}
                placeholder={folderOptions.length ? 'Choose a folder (any level)…' : 'No folders yet'}
                disabled={folderOptions.length === 0}
              />
            </label>
          </div>

          <div className="pm-lookup">
            <button type="button" className="pm-btn" onClick={() => fillFromReceipts()}>
              <FiDownloadCloud size={15} /> Fill from receipts
            </button>
            <span className="pm-hint">{lookupMsg || 'Information from receipts populates the available fields by itself. Press this if fields were not populated, or after edits made since the documents were filed.'}</span>
          </div>

          <div className="pm-section">Document types</div>
          <p className="pm-hint pm-doctypes-lead">
            The kinds of document this {def.singular.toLowerCase()} sends, each with the way the date is printed on it — a gas receipt,
            a till receipt and a statement often differ. Filing uses the matching one to read an unclear date such as 05/06/12.
            A line is added here by itself whenever a filed document shows a date format not listed yet; give it a name and
            the words printed on that kind of document, so Filing can tell it from the others.
          </p>
          <div className="pm-doctypes">
            {firstDocType < 0 && <div className="pm-doctype-none">{addDocTypeBtn}<span className="pm-hint">Add a document type</span></div>}
            {docTypes.map((l, i) => (l.removed ? null : (
              <div className="pm-doctype" key={l.id != null ? `s${l.id}` : `n${i}`}>
                {i === firstDocType ? addDocTypeBtn : <span className="pm-doctype-gap" aria-hidden="true" />}
                <label className="pm-field">
                  <span className="pm-label">Document type</span>
                  <PartyTip text="What you call this kind of document, e.g. Gas receipt, Till receipt, Statement." />
                  <input className="pm-input" type="text" maxLength={100} value={l.name} onChange={e => changeDocType(i, { name: e.target.value })} />
                </label>
                <label className="pm-field">
                  <span className="pm-label">Words printed on it</span>
                  <PartyTip text="Words printed on this kind of document, separated by commas — how Filing recognises it. Leave empty to look for the name itself." />
                  <input className="pm-input" type="text" placeholder="e.g. food court" value={l.words} onChange={e => changeDocType(i, { words: e.target.value })} />
                </label>
                <label className="pm-field">
                  <span className="pm-label">Date format</span>
                  <PartyTip text="How the date is printed on this kind of document. Filing reads an unclear date such as 09/10/09 with it. Set by itself from filed documents." />
                  <select className="pm-input" value={l.format} onChange={e => changeDocType(i, { format: e.target.value })}>
                    <option value="">Not set</option>
                    {dateFormatOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <div className="pm-field">
                  <span className="pm-label">Sample receipt</span>
                  <PartyTip text="A receipt of this kind, to check the date format against. Click to choose one: this party's documents are listed first, any other document can be searched, and each can be looked at before it is used." />
                  {(() => {
                    const doc = l.sample ? accountDocs.find(d => String(d.document_id) === l.sample) : undefined;
                    return (
                      <button type="button" className={`pm-input pm-sample-btn ${l.sample ? '' : 'empty'}`}
                        title={l.sample ? 'Change the sample receipt' : 'Choose a sample receipt'}
                        onClick={() => setPickSampleFor(i)}>
                        {l.sample
                          ? (doc ? docLabel(doc) : `Document ${l.sample}`)
                          : (partyDocOptions.length ? `Choose a receipt… (${partyDocOptions.length} from this ${def.singular.toLowerCase()})` : 'Choose a receipt…')}
                      </button>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  className="pm-link-btn"
                  disabled={!l.sample}
                  title={l.sample ? 'Open this receipt' : 'Choose a sample receipt first'}
                  onClick={() => setViewDocId(Number(l.sample))}
                >
                  <FiFileText size={14} /> View receipt
                </button>
                <button type="button" className="pm-icon-btn pm-danger" title="Remove this document type" onClick={() => removeDocType(i)}><FiTrash2 size={15} /></button>
                <div className="pm-doctype-bk">
                  <div className="pm-field">
                    <span className="pm-label">Nature of Document</span>
                    <PartyTip text="What this kind of document is for, e.g. Meal, Fuel, Office supplies. Filing fills it in for this vendor's documents, and Bookkeeping takes the account head and memo from it. Set by itself when a document of this vendor is filed with a nature." />
                    <SearchableDropdown
                      options={natureOptions}
                      value={l.nature}
                      onChange={v => changeDocType(i, { nature: v, ...(!l.name.trim() && v ? { name: natureOptions.find(o => o.value === v)?.label || '' } : {}) })}
                      placeholder="Meal, Fuel, Office supplies…"
                    />
                  </div>
                  <div className="pm-field">
                    <span className="pm-label">Account head</span>
                    <PartyTip text="The account this vendor's documents of this kind are booked to, e.g. Meals & Entertainment. Bookkeeping fills it in; publishing a document sets it here when it is empty. A shared entry has no account (it belongs to no single set of books)." />
                    <SearchableDropdown
                      options={coaOptions}
                      value={form.shared ? '' : l.coa}
                      onChange={v => changeDocType(i, { coa: v })}
                      placeholder={form.shared ? 'Not for a shared entry' : coaOptions.length ? 'Choose an account…' : 'No chart of accounts for these books yet'}
                      disabled={form.shared || coaOptions.length === 0}
                    />
                  </div>
                </div>
              </div>
            )))}
            {errors.docTypes && <span className="pm-error">{errors.docTypes}</span>}
          </div>

          <div className="pm-section">Contact Person(s)</div>
          <div className="pm-grid">
            {text('contact_name', 'Contact name', { maxLength: 200, tip: 'The person you usually deal with.' })}
            {text('contact_title', 'Title / role', { maxLength: 100, tip: 'e.g. Owner, Accounts receivable, Sales rep.' })}
            {text('phone', 'Office phone', { maxLength: 30, tip: 'The business’s main line.' })}
            {text('mobile', 'Business cell', { maxLength: 30, tip: 'A mobile number for the business or the contact.' })}
            {text('email', 'Email', { type: 'email', maxLength: 255, tip: 'Where statements, orders and remittance advice are sent.' })}
            {text('fax', 'Fax', { maxLength: 30, tip: 'Fax number, if they still use one.' })}
            {text('contact_2_name', 'Second contact', { maxLength: 200, tip: 'Another person to reach when the first is away.' })}
            {text('contact_2_phone', 'Second contact phone', { maxLength: 30, tip: 'The second contact’s phone.' })}
            <div className="pm-span-2">{text('contact_2_email', 'Second contact email', { type: 'email', maxLength: 255, tip: 'The second contact’s email.' })}</div>
          </div>

          <div className="pm-section">Physical business address</div>
          {addressBlock('', 'where the business operates')}

          <div className="pm-section">Business mailing address</div>
          <div className="pm-addr-note">
            <span className="pm-hint">
              {kind === 'vendor' ? 'Where payments and mail are sent (remit-to)' : kind === 'customer' ? 'Where invoices and goods are sent (ship-to)' : 'Where mail is sent'} — fill in only when it is not the physical address.
            </span>
            <button type="button" className="pm-link-btn" onClick={() => copyPhysical('mail_')}>Same as the physical address</button>
          </div>
          {addressBlock('mail_', 'where mail is sent')}

          <div className="pm-section">Home address of contact person</div>
          <div className="pm-addr-note">
            <span className="pm-hint">Where you meet the contact person when the business is run from home.</span>
            <button type="button" className="pm-link-btn" onClick={() => copyPhysical('home_')}>Same as the physical address</button>
          </div>
          {addressBlock('home_', 'the contact person’s home')}

          <div className="pm-section">Accounting</div>
          <div className="pm-grid">
            {text('account_number', kind === 'vendor' ? 'Our account # with them' : kind === 'customer' ? 'Customer account #' : 'Account / reference #', { maxLength: 50, tip: 'The account number this party knows you by — quote it on payments and enquiries.' })}
            {text('tax_id', 'Tax / registration number', { maxLength: 50, tip: 'The party’s sales-tax or business registration number, as printed on its invoices.' })}
            {text('tax_id_2', 'Second registration number', { maxLength: 50, tip: 'A second registration number, where the party prints two.' })}
            {text('payment_terms', 'Payment terms', { list: 'pm-terms', placeholder: 'e.g. Net 30', maxLength: 50, tip: 'When payment is due, e.g. Net 30 = 30 days after the invoice date.' })}
            <label className="pm-field">
              <span className="pm-label">Usual payment method</span>
              <PartyTip text="How this party is usually paid — offered first when its documents are filed." />
              <select className="pm-input" value={form.default_payment_method_id} onChange={e => change('default_payment_method_id', e.target.value)}>
                <option value="">Not set</option>
                {payMethodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="pm-field">
              <span className="pm-label">Currency</span>
              <PartyTip text="The currency this party invoices in. Leave on the books’ currency unless it bills in another." />
              <select className="pm-input" value={form.default_currency} onChange={e => change('default_currency', e.target.value)}>
                <option value="">{baseCurrency ? `Books’ currency — ${baseCurrency}` : 'Select currency'}</option>
                {form.default_currency && !currencyOptions.some(o => o.value === form.default_currency) && <option value={form.default_currency}>{form.default_currency}</option>}
                {currencyOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            {!(form.shared || inSharedView) && (
              <label className="pm-field pm-span-2">
                <span className="pm-label">{kind === 'vendor' ? 'Default expense account' : kind === 'customer' ? 'Default income account' : 'Default account'}</span>
                <PartyTip text="The account this party’s transactions usually go to, in these books." />
                <SearchableDropdown
                  options={coaOptions}
                  value={form.default_coa_id}
                  onChange={v => change('default_coa_id', v)}
                  placeholder={coaOptions.length ? 'Choose an account…' : 'No chart of accounts for these books yet'}
                  disabled={coaOptions.length === 0}
                />
              </label>
            )}
            {text('credit_limit', 'Credit limit', { placeholder: '0.00', maxLength: 16, tip: 'The most that may be owed at one time. A record only.' })}
            {journalOpening ? (
              <label className="pm-field">
                <span className="pm-label">Opening balance</span>
                <PartyTip text={`From journal entry ${journalOpening.ref}: set by the opening-balances journal entry that names this party. Post a newer opening-balances entry to change it.`} />
                <input className="pm-input" type="text" readOnly value={journalOpening.amount.toFixed(2)} />
              </label>
            ) : text('opening_balance', 'Opening balance', {
              placeholder: '0.00', maxLength: 16,
              tip: 'Fills in by itself from an opening-balances journal entry that names this party. Until there is one, type the balance here.',
              hint: kind === 'other' ? undefined
                : form.shared || inSharedView ? 'Shared entry: not added to any Trial Balance.'
                  : `Added to Accounts ${kind === 'customer' ? 'receivable' : 'payable'} on the Trial Balance.`,
            })}
            <label className="pm-field">
              <span className="pm-label">Opening balance as of</span>
              <PartyTip text="The date the opening balance stood at — normally the day your books start." />
              <input
                className="pm-input" type="date" max="9999-12-31" disabled={!!journalOpening}
                value={journalOpening ? journalOpening.date : form.opening_balance_date}
                onChange={e => change('opening_balance_date', e.target.value)}
              />
            </label>
            <label className="pm-field">
              <span className="pm-label">Reconciliation required</span>
              <PartyTip text={`Do you reconcile this ${def.singular.toLowerCase()} on a periodic basis? With Yes, the list shows “Reconcile now” and then the date it was last reconciled.`} />
              <select className="pm-input" value={String(form.reconcile_required)} onChange={e => change('reconcile_required', Number(e.target.value))}>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </label>
            <label className="pm-field">
              <span className="pm-label">Status</span>
              <PartyTip text="An inactive entry is kept but no longer offered when filing or bookkeeping." />
              <select className="pm-input" value={String(form.is_active)} onChange={e => change('is_active', Number(e.target.value))}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
            <label className="pm-check pm-span-2">
              <input type="checkbox" checked={form.tax_slip_required === 1} onChange={e => change('tax_slip_required', e.target.checked ? 1 : 0)} />
              A year-end tax information slip is issued for this {def.singular.toLowerCase()}
            </label>
            <label className="pm-field pm-span-4">
              <span className="pm-label">Notes</span>
              <PartyTip text="Anything worth remembering about this party. For your own use — never printed on documents." />
              <textarea className="pm-input pm-textarea" value={form.notes} onChange={e => change('notes', e.target.value)} rows={2} />
            </label>
            <PartyAttachments lines={attachments} onChange={next => { setFormDirty(true); setAttachments(next); }} accountOwnerId={accountOwnerId} />
          </div>
          <datalist id="pm-terms">{TERMS.map(t => <option key={t} value={t} />)}</datalist>
          <div className="pm-actions">
            <button type="submit" className="pm-btn pm-btn-primary" disabled={busy} title="Save (Ctrl+S)">{busy ? 'Saving…' : `Save ${def.singular}`}</button>
            <button type="button" className="pm-btn" disabled={busy} onClick={leaveForm}>Cancel</button>
          </div>
        </form>
    );
  }

  // Choosing a document type's sample receipt.
  const samplePicker = pickSampleFor != null && form && docTypes[pickSampleFor] ? (
    <SampleReceiptPicker
      documents={accountDocs}
      partyNames={partyNames}
      partyLabel={form.name.trim()}
      value={docTypes[pickSampleFor].sample}
      onPick={id => {
        // The chosen receipt also fills the line: its date format, and its document type
        // as the line's name when the name is still empty (a typed name is kept).
        const line = docTypes[pickSampleFor];
        const doc = id ? accountDocs.find(d => String(d.document_id) === id) : undefined;
        const patch: Partial<DocTypeLine> = { sample: id };
        if (doc && doc.date_format_id != null && doc.date_format_id !== '') patch.format = String(doc.date_format_id);
        // eslint-disable-next-line eqeqeq
        const typeName = doc ? String(docuTypes.find(t => t.docu_type_id == doc.docu_type_id)?.docu_type_name || '').trim() : '';
        if (typeName && !line.name.trim()) patch.name = typeName;
        changeDocType(pickSampleFor, patch);
        setPickSampleFor(null);
      }}
      onClose={() => setPickSampleFor(null)}
    />
  ) : null;

  // The receipt popup (a document type's sample / the receipt an entry was created from).
  const docPopup = viewDocId != null ? (
            <div
              className="pm-doc-overlay"
              role="dialog"
              aria-modal="true"
              aria-label="Receipt"
              onMouseDown={e => { if (e.target === e.currentTarget) setViewDocId(null); }}
              onKeyDown={e => { if (e.key === 'Escape') setViewDocId(null); }}
            >
              <div className="pm-doc-modal">
                <div className="pm-doc-head">
                  <span className="pm-form-title">Receipt{(() => {
                    // which document it is, even when the viewer cannot open it
                    const d = documents.find(x => String(x.document_id) === String(viewDocId));
                    return d ? ` — ${docLabel(d)}` : ` — document ${viewDocId}`;
                  })()}</span>
                  <button type="button" className="pm-icon-btn" title="Close" autoFocus onClick={() => setViewDocId(null)}><FiX size={18} /></button>
                </div>
                <iframe className="pm-doc-frame" title="Receipt" src={`/doc-view?doc=${viewDocId}`} />
              </div>
            </div>
          ) : null;

  // Leaving the quick-add form (X / Cancel) — unsaved entries are confirmed first.
  const leaveForm = async () => {
    if (!(await confirmDiscardChanges())) return;
    if (quickAdd) { setFormDirty(false); quickAdd.onCancel(); } else closeForm();
  };

  if (quickAdd) {
    const kinds = KINDS.filter(k => quickAdd.kinds.includes(k.key));
    return (
      <div className="pm-tab pm-quick">
        <div className="pm-toolbar">
          {kinds.length > 1 && (
            <label className="pm-toolbar-field">
              <span className="pm-label">Add as</span>
              <select className="pm-input" value={kind} onChange={e => { setKind(e.target.value as Kind); setErrors({}); }}>
                {kinds.map(k => <option key={k.key} value={k.key}>{k.singular}</option>)}
              </select>
            </label>
          )}
          <div className="pm-toolbar-field pm-books">
            <span className="pm-label">Books of</span>
            <SearchableDropdown
              options={bookEntities.map(e => ({ value: `${e.owner_type}:${e.owner_id}`, label: e.owner_type === SHARED || e.owner_type === ALL ? e.display_name : `${e.display_name} (${e.badge})` }))}
              value={ownerKey}
              onChange={switchEntity}
              placeholder="Select whose books…"
            />
          </div>
        </div>
        <p className="pm-blurb">{def.blurb}</p>
        {form && renderForm()}
        {docPopup}
      {samplePicker}
      </div>
    );
  }

  return (
    <div className="pm-tab">
      <div className="pm-kinds" role="tablist">
        {KINDS.map(k => (
          <button
            key={k.key}
            type="button"
            role="tab"
            aria-selected={kind === k.key}
            className={`pm-kind ${kind === k.key ? 'active' : ''}`}
            onClick={() => switchKind(k.key)}
          >
            {k.plural} <span className="pm-count">{entityRows(k.key).length}</span>
          </button>
        ))}
      </div>
      <p className="pm-blurb">{def.blurb}</p>

      <div className="pm-toolbar">
        <div className="pm-toolbar-field pm-books">
          <span className="pm-label">Books of</span>
          <SearchableDropdown
            options={bookEntities.map(e => ({ value: `${e.owner_type}:${e.owner_id}`, label: e.owner_type === SHARED || e.owner_type === ALL ? e.display_name : `${e.display_name} (${e.badge})` }))}
            value={ownerKey}
            onChange={switchEntity}
            placeholder="Select whose books…"
          />
        </div>
        <label className="pm-toolbar-field pm-search">
          <span className="pm-label">Search</span>
          <span className="pm-search-wrap">
            <FiSearch className="pm-search-icon" size={15} />
            <input
              className="pm-input"
              type="text"
              value={search}
              placeholder={`Search ${def.plural.toLowerCase()}…`}
              role="combobox"
              aria-expanded={suggestOpen && searchSuggestions.length > 0}
              aria-controls="pm-search-suggest"
              aria-autocomplete="list"
              onChange={e => { setSearch(e.target.value); setSuggestOpen(true); setSuggestIdx(-1); }}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setSuggestOpen(false)}
              onKeyDown={e => {
                if (!suggestOpen || !searchSuggestions.length) { if (e.key === 'ArrowDown') setSuggestOpen(true); return; }
                if (e.key === 'ArrowDown') { e.preventDefault(); setSuggestIdx(i => Math.min(i + 1, searchSuggestions.length - 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setSuggestIdx(i => Math.max(i - 1, -1)); }
                else if (e.key === 'Enter' && suggestIdx >= 0) { e.preventDefault(); pickSuggestion(searchSuggestions[suggestIdx]); }
                else if (e.key === 'Tab' && suggestIdx >= 0) pickSuggestion(searchSuggestions[suggestIdx]);
                else if (e.key === 'Escape') setSuggestOpen(false);
              }}
            />
            {suggestOpen && searchSuggestions.length > 0 && (
              <ul className="pm-suggest" id="pm-search-suggest" role="listbox">
                {searchSuggestions.map((r, i) => (
                  <React.Fragment key={r[def.pk]}>
                  {i === firstSimilar && <li className="pm-suggest-head" role="presentation">Did you mean</li>}
                  <li
                    role="option"
                    aria-selected={i === suggestIdx}
                    className={`pm-suggest-item ${i === suggestIdx ? 'active' : ''}`}
                    onMouseDown={e => { e.preventDefault(); pickSuggestion(r); }}   /* before the input's blur */
                    onMouseEnter={() => setSuggestIdx(i)}
                  >
                    <span>{r[def.nameCol]}</span>
                    {(r.nickname || r.alias_names) && <span className="pm-suggest-sub">{[r.nickname, r.alias_names].filter(Boolean).join(' · ')}</span>}
                  </li>
                  </React.Fragment>
                ))}
              </ul>
            )}
          </span>
        </label>
        <label className="pm-check">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> Show inactive
        </label>
        <button
          type="button"
          className="pm-btn"
          onClick={openImport}
          disabled={importCandidates.length === 0}
          title={importCandidates.length === 0
            ? 'Every party on this set of books\u2019 filed documents is already in one of its lists'
            : `Add the parties named on filed documents as ${def.plural.toLowerCase()}`}
        >
          <FiDownloadCloud size={16} /> From filed documents <span className="pm-count">{importCandidates.length}</span>
        </button>
        <button type="button" className="pm-btn pm-btn-primary" onClick={openAdd}>
          <FiPlus size={16} /> Add {def.singular}
        </button>
      </div>

      {importOpen && (
        <div className="pm-form pm-import">
          <div className="pm-form-head">
            <span className="pm-form-title">Add {def.plural.toLowerCase()} from filed documents — {entity?.display_name}</span>
            <button type="button" className="pm-icon-btn" title="Close" onClick={() => setImportOpen(false)}><FiX size={18} /></button>
          </div>
          <p className="pm-hint">
            Parties named on documents already filed for these books that are in none of its lists yet
            {kind === 'vendor' ? ' (not from sales-side documents)' : kind === 'customer' ? ' (not from purchase-side documents)' : ''}.
            Untick anything that does not belong under {def.plural}. Address, phone and email are filled in where they were read off the receipts.
          </p>
          <div className="pm-table-wrap">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={importCandidates.length > 0 && importCandidates.every(c => importPicked.has(c.key))}
                      onChange={e => setImportPicked(e.target.checked ? new Set(importCandidates.map(c => c.key)) : new Set())}
                    />
                  </th>
                  <th>Party on the documents</th>
                  <th>Documents</th>
                  <th>Latest</th>
                  <th>Document type</th>
                </tr>
              </thead>
              <tbody>
                {importCandidates.map(c => (
                  <tr key={c.key}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Add ${c.name}`}
                        checked={importPicked.has(c.key)}
                        onChange={e => setImportPicked(prev => { const n = new Set(prev); if (e.target.checked) n.add(c.key); else n.delete(c.key); return n; })}
                      />
                    </td>
                    <td>{c.name}</td>
                    <td>{c.docs}</td>
                    <td>{c.lastDate}</td>
                    <td>{Array.from(c.types).filter(Boolean).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pm-actions">
            <button type="button" className="pm-btn pm-btn-primary" disabled={busy || importPicked.size === 0} onClick={runImport}>
              {busy ? 'Adding…' : `Add ${importPicked.size} ${(importPicked.size === 1 ? def.singular : def.plural).toLowerCase()}`}
            </button>
            <button type="button" className="pm-btn" disabled={busy} onClick={() => setImportOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {msg && <div className="pm-msg" role="status">{msg}</div>}

      {/* a new entry, or one whose row is not in the list on screen, opens up here */}
      {/* eslint-disable-next-line eqeqeq */}
      {form && !(editingId != null && visibleRows.some(r => r[def.pk] == editingId)) && renderForm()}

      {visibleRows.length === 0 ? (
        <div className="pm-empty">
          {entityRows(kind).length === 0
            ? `No ${def.plural.toLowerCase()} for ${entity?.display_name} yet. Use “Add ${def.singular}” to create the first one.`
            : `No ${def.plural.toLowerCase()} match.`}
        </div>
      ) : (
        <div className="pm-table-wrap" ref={listRef}>
          <table className="pm-table">
            <thead>
              <tr>
                {sortHeader('txns', 'Txns', 'Transactions: documents naming this party + journal entries tagged with it')}
                {sortHeader('name', 'Name')}
                {kind === 'other' && sortHeader('type', 'Type')}
                {sortHeader('contact', 'Contact')}
                {sortHeader('phone', 'Phone')}
                {sortHeader('email', 'Email')}
                {sortHeader('city', 'City')}
                {sortHeader('terms', 'Terms')}
                {sortHeader('currency', 'Currency')}
                {sortHeader('reconciled', <>Reconciled <span className="pm-th-tip" aria-hidden="true">!</span></>,
                  'To see reconciliation status, enter YES in the Reconciliation Required field')}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(r => {
                const id = r[def.pk];
                // eslint-disable-next-line eqeqeq
                const inactive = r.is_active == 0;
                return (
                  <React.Fragment key={id}>
                    {/* eslint-disable-next-line eqeqeq */}
                    <tr data-party-id={String(id)} className={`${inactive ? 'pm-row-inactive' : ''} ${editingId == id ? 'pm-row-editing' : ''} ${r.verify_status === UNVERIFIED ? 'pm-row-unverified' : ''}`}>
                      {(() => {
                        const c = txnCounts.get(String(id)) || { docs: 0, toFile: 0, journals: 0 };
                        const n = c.docs + c.journals;
                        return (
                          <td className="pm-txn-cell">
                            <span className={`pm-txn-count ${n ? '' : 'zero'}`}
                              title={`${c.docs} document${c.docs === 1 ? '' : 's'}${c.toFile ? ` (${c.toFile} still to file)` : ''} · ${c.journals} journal entr${c.journals === 1 ? 'y' : 'ies'}`}>
                              {n}
                            </span>
                          </td>
                        );
                      })()}
                      <td>
                        <button type="button" className="pm-name-link" onClick={() => openEdit(r, def)} title={`Edit ${r[def.nameCol]}`}>
                          {r[def.nameCol]}
                        </button>
                        {r.nickname && <span className="pm-nick" title="Nickname / short name — used when naming files">{r.nickname}</span>}
                        {r[def.codeCol] && <span className="pm-code">{r[def.codeCol]}</span>}
                        {r.tags && (
                          <span className="ti-row-tags">
                            {parseTags(r.tags).map(t => <span key={t} className="ti-row-tag">{t}</span>)}
                          </span>
                        )}
                        {r.owner_type === SHARED && entity?.owner_type !== SHARED && <span className="pm-badge pm-badge-shared" title="Shared — listed in all of your books">Shared</span>}
                        {inactive && <span className="pm-badge">Inactive</span>}
                        {inAllView && r.owner_type !== SHARED && (
                          // eslint-disable-next-line eqeqeq
                          <span className="pm-badge pm-badge-books" title={`Books of ${ownerLabel(r).full}`}>{ownerLabel(r).short}</span>
                        )}
                        {r.verify_status === UNVERIFIED && (
                          <>
                            <span className="pm-badge pm-badge-check" title="Created from a receipt — open it, check and complete the details, then Save">Check details</span>
                            {r.source_document_id != null && (
                              <button type="button" className="pm-link-btn" title="Open the receipt this entry was created from" onClick={() => setViewDocId(Number(r.source_document_id))}>
                                <FiFileText size={14} /> View receipt
                              </button>
                            )}
                          </>
                        )}
                      </td>
                      {kind === 'other' && <td>{PARTY_TYPES.find(t => t.value === r.party_type)?.label || 'Other'}</td>}
                      <td>{r.contact_name || ''}</td>
                      <td>{r.phone || r.mobile || ''}</td>
                      <td>{r.email || ''}</td>
                      <td>{r.city || ''}</td>
                      <td>{r.payment_terms || ''}</td>
                      <td>{String(r.default_currency || '').trim() || baseCurrency}</td>
                      <td>
                        {/* eslint-disable-next-line eqeqeq */}
                        {r.reconcile_required == 1 && (() => {
                          const last = lastReconciled.get(String(id));
                          if (!last) {
                            return (
                              <button type="button" className="pm-link-btn" title={`${r[def.nameCol]} has not been reconciled yet — start now`} onClick={() => setReconcileRow(r)}>
                                <FiCheckSquare size={14} /> Reconcile now
                              </button>
                            );
                          }
                          return (
                            <button
                              type="button"
                              className="pm-link-btn"
                              title={last.difference === 0
                                ? `Last reconciled to ${last.date} — click to reconcile again`
                                : `Last reconciled to ${last.date}, saved with a difference of ${Math.abs(last.difference).toFixed(2)} still to find — click to continue`}
                              onClick={() => setReconcileRow(r)}
                            >
                              {last.date}{last.difference !== 0 && <span className="pm-rec-off"> (difference)</span>}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="pm-row-actions">
                        <button type="button" className="pm-icon-btn" title="Edit" onClick={() => openEdit(r, def)}><FiEdit2 size={15} /></button>
                        <button type="button" className="pm-icon-btn pm-danger" title="Remove" onClick={() => setConfirmDeleteId(id)}><FiTrash2 size={15} /></button>
                      </td>
                    </tr>
                    {/* the entry being edited: its form right under its row */}
                    {/* eslint-disable-next-line eqeqeq */}
                    {form && editingId == id && (
                      <tr className="pm-form-row">
                        <td colSpan={kind === 'other' ? 12 : 11}>{renderForm()}</td>
                      </tr>
                    )}
                    {/* eslint-disable-next-line eqeqeq */}
                    {confirmDeleteId == id && (
                      <tr className="pm-confirm-row">
                        <td colSpan={kind === 'other' ? 12 : 11}>
                          Remove “{r[def.nameCol]}” from {entity?.display_name}? Entries already recorded keep the name.
                          <button type="button" className="pm-btn pm-btn-danger" disabled={busy} onClick={() => confirmDelete(r)}>{busy ? 'Removing…' : 'Remove'}</button>
                          <button type="button" className="pm-btn" disabled={busy} onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {docPopup}
      {samplePicker}
      {reconcileRow && (
        <PartyReconcileModal
          kind={kind}
          partyId={Number(reconcileRow[def.pk])}
          partyName={String(reconcileRow[def.nameCol] || '')}
          manualOpening={reconcileRow.opening_balance}
          manualOpeningDate={reconcileRow.opening_balance_date ? String(reconcileRow.opening_balance_date).slice(0, 10) : null}
          accountOwnerId={accountOwnerId}
          userId={currentUser ? Number(currentUser.user_id) : null}
          // a shared party can have a ledger in every set of books; any other, in its own
          books={reconcileRow.owner_type === SHARED ? ownerEntities
            // eslint-disable-next-line eqeqeq
            : ownerEntities.filter(e => e.owner_type === reconcileRow.owner_type && e.owner_id == reconcileRow.owner_id)}
          defaultBooksKey={inSharedView ? undefined : ownerKey}
          currency={String(reconcileRow.default_currency || '').trim() || baseCurrency}
          onClose={() => {
            setReconcileRow(null);
            loadTable('acctg_party_reconcile').catch(() => getTableData('acctg_party_reconcile')).then(setReconcileRows);
          }}
        />
      )}
    </div>
  );
};

export default PartiesMasterTab;
